<template>
  <div class="service-volumetry">
    <v-row>
      <v-col cols="12">
        <div class="d-flex flex-wrap align-center justify-space-between mb-2">
          <div>
            <v-chip
              class="mb-2"
              color="primary"
              outlined
              small
              label
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiChartBar }}
              </v-icon>
              Volumetria de Pessoas Físicas
            </v-chip>
            <p class="text-2xl mb-1">
              Descubra o tamanho do seu mercado
            </p>
            <p class="mb-0 grey--text text--darken-1">
              Configure os filtros e receba o volume estimado do público desejado.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Tela de sucesso (pós-envio) -->
    <template v-if="successScreen">
      <v-row justify="center">
        <v-col
          cols="12"
          md="8"
          lg="6"
        >
          <v-card
            outlined
            class="text-center"
          >
            <v-card-text class="py-10">
              <v-avatar
                size="72"
                class="mb-5 vol-avatar"
              >
                <v-icon large>
                  {{ successScreen.tipo === 'fila' ? icons.mdiPartyPopper : icons.mdiFileDocumentOutline }}
                </v-icon>
              </v-avatar>

              <p class="text-h6 font-weight-bold mb-1">
                {{ successScreen.tipo === 'fila' ? 'Consulta na fila!' : 'Pedido enviado!' }}
              </p>

              <p
                v-if="successScreen.nome"
                class="grey--text text--darken-1 mb-2"
              >
                {{ successScreen.nome }}
              </p>

              <v-chip
                v-if="successScreen.id"
                color="primary"
                label
                outlined
                small
                class="mb-6"
              >
                {{ successScreen.tipo === 'fila' ? 'Fila' : 'Pedido' }} #{{ successScreen.id }}
              </v-chip>

              <div class="text-left mx-auto mb-6 success-next-steps">
                <template v-if="successScreen.tipo === 'fila'">
                  <p class="mb-2">
                    <strong>1.</strong> Seu pedido entrou na fila de processamento.
                  </p>
                  <p class="mb-2">
                    <strong>2.</strong> O volume estimado aparece na aba "Lista de volumetrias criadas".
                  </p>
                  <p class="mb-0">
                    <strong>3.</strong> A lista se atualiza automaticamente enquanto o pedido estiver em andamento.
                  </p>
                </template>
                <template v-else>
                  <p class="mb-2">
                    <strong>1.</strong> Nosso time vai analisar a descrição do perfil enviado.
                  </p>
                  <p class="mb-2">
                    <strong>2.</strong> Você receberá um orçamento com valor e prazo.
                  </p>
                  <p class="mb-0">
                    <strong>3.</strong> Acompanhe o status na aba "Lista de volumetrias criadas". A aprovação do orçamento é feita pelo time Ligdata.
                  </p>
                </template>
              </div>

              <div class="d-flex flex-wrap justify-center">
                <v-btn
                  color="primary"
                  class="ma-1"
                  @click="clearSuccessScreen(0)"
                >
                  <v-icon left>
                    {{ icons.mdiPlus }}
                  </v-icon>
                  Nova consulta
                </v-btn>
                <v-btn
                  outlined
                  color="primary"
                  class="ma-1"
                  @click="clearSuccessScreen(1)"
                >
                  <v-icon left>
                    {{ icons.mdiTextBoxOutline }}
                  </v-icon>
                  Acompanhar
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Wizard de filtros -->
    <template v-else-if="wizardActive">
      <v-row>
        <v-col cols="12">
          <div class="d-flex flex-wrap align-center justify-space-between mb-2">
            <div class="d-flex align-center mb-2 mb-sm-0">
              <v-avatar
                size="28"
                class="me-2 vol-avatar"
              >
                <span class="text-caption font-weight-bold">
                  {{ flowMode === 'direto' ? '!' : currentStep }}
                </span>
              </v-avatar>
              <span class="grey--text text--darken-1">
                {{ flowMode === 'direto' ? 'Pedido personalizado' : `Etapa ${currentStep} de ${totalSteps}` }}
              </span>
              <v-chip
                v-if="flowMode !== 'b2c'"
                color="secondary"
                x-small
                label
                outlined
                class="ms-2"
              >
                Personalizada
              </v-chip>
            </div>

            <v-btn
              color="error"
              text
              small
              class="mb-2 mb-sm-0"
              @click="cancelAndRestart"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiClose }}
              </v-icon>
              Cancelar e Reiniciar
            </v-btn>
          </div>

          <div
            v-if="flowMode !== 'direto'"
            class="d-flex align-center mb-6"
          >
            <v-progress-linear
              :value="progressPercent"
              color="primary"
              height="6"
              rounded
              class="me-3"
            />
            <span class="grey--text text-caption text-no-wrap">
              {{ progressPercent }}% completo
            </span>
          </div>

          <!-- Etapa 1: Sexo -->
          <template v-if="currentStep === 1">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Qual o sexo do público?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Filtre por sexo biológico ou inclua todos
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-row dense>
              <v-col
                v-for="option in genderOptions"
                :key="option.value"
                cols="12"
                sm="6"
              >
                <v-card
                  outlined
                  class="option-card"
                  :class="{ 'option-card--selected': filters.genero === option.value }"
                  @click="selectGender(option.value)"
                >
                  <v-list-item class="py-3">
                    <v-list-item-avatar
                      rounded
                      :class="optionAvatarClass(filters.genero === option.value)"
                    >
                      <v-icon>
                        {{ option.icon }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        {{ option.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ option.subtitle }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card>
              </v-col>
            </v-row>

            <div class="d-flex justify-center mt-6">
              <v-btn
                text
                color="grey darken-1"
                @click="goBack"
              >
                <v-icon left>
                  {{ icons.mdiArrowLeft }}
                </v-icon>
                Voltar
              </v-btn>
            </div>
          </template>

          <!-- Etapa 2: Faixa etária -->
          <template v-else-if="currentStep === 2">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Qual a faixa etária desejada?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Define a idade mínima e máxima do público
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-card outlined>
              <v-card-text class="pt-5">
                <v-row dense>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="filters.idadeMin"
                      label="Idade mínima"
                      type="number"
                      min="0"
                      max="120"
                      outlined
                      dense
                      hide-details="auto"
                      :error-messages="ageErrors.min"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="filters.idadeMax"
                      label="Idade máxima"
                      type="number"
                      min="0"
                      max="120"
                      outlined
                      dense
                      hide-details="auto"
                      :error-messages="ageErrors.max"
                    />
                  </v-col>
                </v-row>

                <p class="text-caption grey--text mb-2 mt-5">
                  Atalhos comuns
                </p>
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="shortcut in ageShortcuts"
                    :key="shortcut.label"
                    class="me-2 mb-2"
                    :color="isAgeShortcutActive(shortcut) ? 'primary' : undefined"
                    :outlined="!isAgeShortcutActive(shortcut)"
                    label
                    @click="applyAgeShortcut(shortcut)"
                  >
                    {{ shortcut.label }}
                  </v-chip>
                </div>

                <div class="d-flex flex-wrap align-center justify-space-between mt-6">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    Voltar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    @click="confirmAgeRange"
                  >
                    Confirmar
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </template>

          <!-- Etapa 3: Renda -->
          <template v-else-if="currentStep === 3">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Qual a faixa de renda mensal?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Estimativa baseada em dados de renda declarada e modelos preditivos
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-row dense>
              <v-col
                v-for="option in incomeOptions"
                :key="option.value"
                cols="12"
                sm="6"
              >
                <v-card
                  outlined
                  class="option-card"
                  :class="{ 'option-card--selected': filters.rendaPreset === option.value }"
                  @click="selectIncome(option)"
                >
                  <v-list-item class="py-3">
                    <v-list-item-avatar
                      rounded
                      :class="optionAvatarClass(filters.rendaPreset === option.value)"
                    >
                      <v-icon>
                        {{ option.icon }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold text-wrap">
                        {{ option.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        {{ option.subtitle }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card>
              </v-col>
            </v-row>

            <v-card
              v-if="filters.rendaPreset === 'personalizado'"
              outlined
              class="mt-4"
            >
              <v-card-text class="pt-5">
                <p class="subtitle-2 font-weight-medium mb-3">
                  Defina a faixa de renda mensal (R$)
                </p>
                <v-row dense>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="filters.rendaMin"
                      label="Renda mínima (R$)"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details="auto"
                      prefix="R$"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="filters.rendaMax"
                      label="Renda máxima (R$)"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details="auto"
                      prefix="R$"
                    />
                  </v-col>
                </v-row>

                <p
                  v-if="incomeErrors.length"
                  class="error--text text-caption mt-2 mb-0"
                >
                  {{ incomeErrors[0] }}
                </p>

                <p
                  v-else
                  class="text-caption grey--text mt-2 mb-0"
                >
                  Deixe um dos campos vazio para usar apenas o limite informado.
                </p>

                <div class="d-flex flex-wrap align-center justify-space-between mt-6">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    Voltar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    @click="confirmCustomIncome"
                  >
                    Continuar
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <div
              v-else
              class="d-flex justify-center mt-6"
            >
              <v-btn
                text
                color="grey darken-1"
                @click="goBack"
              >
                <v-icon left>
                  {{ icons.mdiArrowLeft }}
                </v-icon>
                Voltar
              </v-btn>
            </div>
          </template>

          <!-- Etapa 4: Localização -->
          <template v-else-if="currentStep === 4">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Qual a localização do público?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Selecione estados e, opcionalmente, cidades específicas
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-card outlined>
              <v-card-text class="pt-5">
                <div class="d-flex flex-wrap align-center justify-space-between mb-3">
                  <span class="subtitle-2 font-weight-medium">
                    Estados <span class="error--text">*</span>
                  </span>
                  <div>
                    <v-btn
                      text
                      x-small
                      color="primary"
                      class="me-1"
                      :disabled="neighborhoodModeActive"
                      @click="selectAllStates"
                    >
                      Todos
                    </v-btn>
                    <span class="grey--text text--lighten-1">|</span>
                    <v-btn
                      text
                      x-small
                      color="primary"
                      class="ms-1"
                      :disabled="neighborhoodModeActive"
                      @click="clearStates"
                    >
                      Limpar
                    </v-btn>
                  </div>
                </div>

                <div class="d-flex flex-wrap mb-2">
                  <v-btn
                    v-for="uf in brazilianStates"
                    :key="uf"
                    class="state-chip me-2 mb-2"
                    :color="isStateSelected(uf) ? 'primary' : undefined"
                    :outlined="!isStateSelected(uf)"
                    :disabled="neighborhoodModeActive"
                    small
                    depressed
                    @click="toggleState(uf)"
                  >
                    {{ uf }}
                  </v-btn>
                </div>

                <p
                  class="text-caption mb-1"
                  :class="locationErrors.ufs.length ? 'error--text' : 'grey--text'"
                >
                  {{
                    locationErrors.ufs.length
                      ? locationErrors.ufs[0]
                      : `${filters.ufs.length} estado(s) selecionado(s)`
                  }}
                </p>

                <p class="subtitle-2 font-weight-medium mt-6 mb-3">
                  Cidades e bairros
                  <span class="grey--text font-weight-regular">(opcional)</span>
                </p>

                <v-alert
                  v-if="neighborhoodModeActive"
                  dense
                  colored-border
                  border="left"
                  color="primary"
                  class="mb-3"
                >
                  Modo bairro ativo — remova os bairros abaixo para alterar cidade ou estado.
                </v-alert>

                <v-alert
                  v-if="!filters.ufs.length"
                  dense
                  text
                  color="grey"
                  class="mb-3"
                >
                  Selecione pelo menos um estado acima para habilitar a busca de cidades.
                </v-alert>

                <v-autocomplete
                  v-model="filters.cidades"
                  :items="citySearchItems"
                  :search-input.sync="citySearch"
                  :loading="citiesLoading"
                  :disabled="!filters.ufs.length || neighborhoodModeActive"
                  :filter="filterCitiesAccentInsensitive"
                  label="Buscar cidade"
                  placeholder="Digite o nome da cidade"
                  outlined
                  dense
                  hide-details
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  item-text="label"
                  item-value="id"
                  return-object
                  class="mb-3"
                  :prepend-inner-icon="icons.mdiMapMarkerOutline"
                  no-data-text="Nenhuma cidade encontrada"
                  @focus="ensureCitiesLoaded"
                />

                <template v-if="filters.cidades.length === 1">
                  <p class="subtitle-2 font-weight-medium mb-2">
                    Refinar por bairro
                    <span class="grey--text font-weight-regular">(opcional)</span>
                  </p>
                  <v-autocomplete
                    v-model="bairroDraft"
                    :items="bairroSuggestions"
                    :search-input.sync="bairroSearch"
                    :loading="bairrosLoading"
                    label="Buscar bairro"
                    :placeholder="`Buscar bairro em ${filters.cidades[0].name || filters.cidades[0].nome}...`"
                    outlined
                    dense
                    hide-details
                    clearable
                    item-text="name"
                    item-value="id"
                    return-object
                    class="mb-3"
                    :prepend-inner-icon="icons.mdiMagnify"
                    no-data-text="Digite ao menos 2 caracteres para buscar bairros"
                    @update:search-input="onBairroSearchInput"
                    @change="onBairroDraftSelected"
                  />
                </template>

                <div
                  v-if="filters.bairros.length"
                  class="d-flex flex-wrap mb-3"
                >
                  <v-chip
                    v-for="bairro in filters.bairros"
                    :key="bairro.id"
                    class="me-2 mb-2"
                    close
                    color="primary"
                    outlined
                    small
                    @click:close="removeBairro(bairro.id)"
                  >
                    {{ bairro.name }}
                  </v-chip>
                  <v-btn
                    text
                    x-small
                    color="error"
                    class="mb-2"
                    @click="clearAllNeighborhoods"
                  >
                    Limpar bairros
                  </v-btn>
                </div>

                <div
                  class="zone-panel mb-2"
                  :class="{ 'zone-panel--open': zonePanelOpen }"
                >
                  <button
                    type="button"
                    class="zone-panel__header"
                    :disabled="!filters.ufs.length"
                    @click="toggleZonePanel"
                  >
                    <div class="d-flex align-center">
                      <v-icon
                        color="primary"
                        small
                        class="me-2"
                      >
                        {{ icons.mdiMapMarkerOutline }}
                      </v-icon>
                      <span class="primary--text font-weight-medium text-uppercase text-caption">
                        Selecionar por zona ou região
                      </span>
                      <v-chip
                        v-if="filters.bairros.length"
                        color="primary"
                        small
                        class="ms-2"
                      >
                        {{ filters.bairros.length }} bairros
                      </v-chip>
                    </div>
                    <v-icon
                      color="primary"
                      small
                    >
                      {{ zonePanelOpen ? icons.mdiChevronUp : icons.mdiChevronDown }}
                    </v-icon>
                  </button>

                  <div
                    v-if="zonePanelOpen"
                    class="zone-panel__body"
                  >
                    <template v-if="availableZoneRegions.length">
                      <div
                        v-for="region in availableZoneRegions"
                        :key="region.id"
                        class="mb-4"
                      >
                        <button
                          type="button"
                          class="zone-region-header"
                          @click="toggleRegion(region.id)"
                        >
                          <span class="font-weight-bold text-uppercase">
                            {{ region.title }}
                          </span>
                          <v-icon small>
                            {{ isRegionExpanded(region.id) ? icons.mdiChevronUp : icons.mdiChevronDown }}
                          </v-icon>
                        </button>

                        <v-row
                          v-if="isRegionExpanded(region.id)"
                          dense
                          class="mt-2"
                        >
                          <v-col
                            v-for="zone in region.zones"
                            :key="zone.id"
                            cols="12"
                            md="6"
                          >
                            <v-card
                              outlined
                              class="zone-card"
                              :class="{
                                'zone-card--selected': getZoneSelectedCount(zone) === zone.neighborhoods.length,
                                'zone-card--partial': getZoneSelectedCount(zone) > 0
                                  && getZoneSelectedCount(zone) < zone.neighborhoods.length,
                              }"
                            >
                              <v-card-text class="pb-3">
                                <div class="d-flex align-start justify-space-between mb-2">
                                  <div>
                                    <div class="font-weight-bold">
                                      {{ zone.title }}
                                    </div>
                                    <div class="text-caption grey--text">
                                      {{ zone.neighborhoods.length }} bairros/cidades
                                      <span
                                        v-if="getZoneSelectedCount(zone)"
                                        class="font-weight-bold"
                                        :class="getZoneSelectedCount(zone) === zone.neighborhoods.length
                                          ? 'warning--text'
                                          : 'error--text'"
                                      >
                                        {{ getZoneSelectedCount(zone) }} adicionado{{ getZoneSelectedCount(zone) > 1 ? 's' : '' }}
                                      </span>
                                    </div>
                                  </div>

                                  <v-btn
                                    v-if="getZoneSelectedCount(zone) === zone.neighborhoods.length"
                                    outlined
                                    x-small
                                    color="error"
                                    @click="removeZoneNeighborhoods(zone)"
                                  >
                                    <v-icon
                                      left
                                      x-small
                                    >
                                      {{ icons.mdiClose }}
                                    </v-icon>
                                    Remover
                                  </v-btn>
                                  <v-btn
                                    v-else
                                    outlined
                                    x-small
                                    color="primary"
                                    @click="addZoneNeighborhoods(zone, region)"
                                  >
                                    <v-icon
                                      left
                                      x-small
                                    >
                                      {{ icons.mdiPlus }}
                                    </v-icon>
                                    Adicionar tudo
                                  </v-btn>
                                </div>

                                <p class="text-caption grey--text mb-0 zone-card__preview">
                                  {{ getZonePreview(zone) }}
                                </p>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>

                      <div
                        v-if="filters.bairros.length"
                        class="d-flex align-center justify-space-between mt-2 pt-3 zone-panel__footer"
                      >
                        <span class="primary--text text-caption font-weight-medium">
                          <v-icon
                            color="primary"
                            x-small
                            class="me-1"
                          >
                            {{ icons.mdiCheck }}
                          </v-icon>
                          {{ filters.bairros.length }} bairros selecionados
                        </span>
                        <v-btn
                          text
                          x-small
                          color="error"
                          @click="clearAllNeighborhoods"
                        >
                          Limpar todos
                        </v-btn>
                      </div>
                    </template>

                    <p
                      v-else
                      class="grey--text text-caption mb-0 pa-2"
                    >
                      Nenhuma zona pré-definida para os estados selecionados.
                      Use a busca de cidade/bairro acima.
                    </p>
                  </div>
                </div>

                <div class="d-flex flex-wrap align-center justify-space-between mt-6">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    Voltar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    @click="confirmLocation"
                  >
                    Confirmar
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </template>

          <!-- Etapa 5: Canais de contato -->
          <template v-else-if="currentStep === 5">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Quais canais de contato são necessários?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    O registro precisa ter pelo menos os canais selecionados
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-row dense>
              <v-col
                v-for="option in channelOptions"
                :key="option.value"
                cols="12"
              >
                <v-card
                  outlined
                  class="option-card"
                  :class="{ 'option-card--selected': isChannelSelected(option.value) }"
                  @click="toggleChannel(option.value)"
                >
                  <v-list-item class="py-3">
                    <v-list-item-avatar
                      rounded
                      :class="optionAvatarClass(isChannelSelected(option.value))"
                    >
                      <v-icon>
                        {{ option.icon }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        {{ option.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ option.subtitle }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon :class="isChannelSelected(option.value) ? 'vol-icon-accent' : 'vol-icon-muted'">
                        {{ isChannelSelected(option.value) ? icons.mdiCheckCircle : icons.mdiCheckboxBlankCircleOutline }}
                      </v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-card>
              </v-col>
            </v-row>

            <p
              v-if="channelErrors.length"
              class="error--text text-caption mt-2 mb-0"
            >
              {{ channelErrors[0] }}
            </p>

            <div class="d-flex flex-wrap align-center justify-space-between mt-6">
              <v-btn
                outlined
                color="grey darken-1"
                class="mb-2 me-2"
                @click="goBack"
              >
                <v-icon left>
                  {{ icons.mdiArrowLeft }}
                </v-icon>
                Voltar
              </v-btn>
              <v-btn
                color="primary"
                class="mb-2 flex-grow-1"
                style="max-width: 420px;"
                @click="confirmChannels"
              >
                Continuar
                <v-icon right>
                  {{ icons.mdiArrowRight }}
                </v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Etapa 6: CBO / Profissão -->
          <template v-else-if="currentStep === 6">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Filtrar por ocupação/profissão (CBO)?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Digite ou cole uma lista de códigos CBO para filtrar por ocupação profissional
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-card outlined>
              <v-card-text class="pt-5">
                <p class="text-caption grey--text mb-3">
                  Consultas B2C hoje: {{ b2cQuotaUsed }} / {{ b2cQuotaLimit }}
                </p>

                <button
                  type="button"
                  class="cbo-search-trigger mb-4"
                  @click="openCboSearchModal"
                >
                  <div class="d-flex align-center">
                    <v-avatar
                      size="36"
                      class="me-3 vol-avatar"
                    >
                      <v-icon small>
                        {{ icons.mdiMagnify }}
                      </v-icon>
                    </v-avatar>
                    <div class="text-left">
                      <div class="primary--text font-weight-bold">
                        Buscar por nome da profissão
                      </div>
                      <div class="primary--text text-caption">
                        Digite "médico", "engenheiro", "motorista"... e veja os CBOs por família
                      </div>
                    </div>
                  </div>
                  <v-icon color="primary">
                    {{ icons.mdiArrowRight }}
                  </v-icon>
                </button>

                <p class="text-center grey--text text-caption mb-3">
                  ou cole os códigos diretamente
                </p>

                <div class="d-flex flex-wrap align-start">
                  <v-textarea
                    v-model="cboPasteText"
                    class="flex-grow-1 me-sm-3 mb-2"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    hide-details="auto"
                    :error-messages="cboPasteError"
                    placeholder="Cole códigos CBO: 211205, 211210&#10;Shift+Enter para nova linha, Enter para adicionar"
                    @keydown.enter.exact.prevent="validateAndAddCboCodes"
                  />
                  <v-btn
                    outlined
                    color="primary"
                    class="mb-2"
                    :disabled="!cboPasteText.trim()"
                    @click="validateAndAddCboCodes"
                  >
                    Validar e Adicionar
                  </v-btn>
                </div>

                <div
                  v-if="filters.cbos.length"
                  class="d-flex flex-wrap mt-3"
                >
                  <v-chip
                    v-for="cbo in filters.cbos"
                    :key="cbo.code"
                    class="me-2 mb-2"
                    close
                    label
                    @click:close="removeCbo(cbo.code)"
                  >
                    {{ cbo.code }} — {{ cbo.name }}
                  </v-chip>
                </div>

                <div class="d-flex flex-wrap align-center justify-space-between mt-6">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    Voltar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    @click="confirmCbos"
                  >
                    Continuar
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </template>

          <!-- Etapa 7: Blacklist / consultar -->
          <template v-else-if="currentStep === 7">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiChartBar }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Deseja excluir alguma ocupação?
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Selecione ou crie uma blacklist de CBOs para remover da contagem
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-card outlined>
              <v-card-text class="pt-5">
                <p class="text-caption grey--text mb-3">
                  Consultas B2C hoje: {{ b2cQuotaUsed }} / {{ b2cQuotaLimit }}
                </p>

                <div
                  class="blacklist-panel mb-4"
                  :class="{ 'blacklist-panel--open': blacklistPanelOpen }"
                >
                  <button
                    type="button"
                    class="blacklist-panel__header"
                    @click="blacklistPanelOpen = !blacklistPanelOpen"
                  >
                    <div class="d-flex align-center">
                      <v-icon
                        color="error"
                        small
                        class="me-2"
                      >
                        {{ icons.mdiCancel }}
                      </v-icon>
                      <span class="error--text font-weight-medium text-uppercase text-caption">
                        Excluir ocupações (blacklist)
                      </span>
                      <v-chip
                        v-if="filters.cbosExcluidos.length"
                        color="error"
                        small
                        class="ms-2"
                      >
                        {{ filters.cbosExcluidos.length }} CBOs excluídos
                      </v-chip>
                    </div>
                    <v-icon
                      color="error"
                      small
                    >
                      {{ blacklistPanelOpen ? icons.mdiChevronUp : icons.mdiChevronDown }}
                    </v-icon>
                  </button>

                  <div
                    v-if="blacklistPanelOpen"
                    class="blacklist-panel__body"
                  >
                    <template v-if="filters.cbosExcluidos.length">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <span class="font-weight-medium">
                          {{ filters.cbosExcluidos.length }} CBOs excluídos da contagem
                        </span>
                        <v-btn
                          text
                          x-small
                          color="error"
                          @click="clearExcludedCbos"
                        >
                          Limpar tudo
                        </v-btn>
                      </div>
                      <div class="d-flex flex-wrap mb-4">
                        <v-chip
                          v-for="cbo in filters.cbosExcluidos"
                          :key="`ex-${cbo.code}`"
                          class="me-2 mb-2"
                          close
                          label
                          @click:close="removeExcludedCbo(cbo.code)"
                        >
                          <span class="error--text font-weight-bold me-1">{{ cbo.code }}</span>
                          {{ truncateLabel(cbo.name, 22) }}
                        </v-chip>
                      </div>
                    </template>

                    <div class="d-flex align-center justify-space-between mb-3">
                      <span class="subtitle-2 font-weight-medium mb-0">
                        Blacklists salvas
                      </span>
                      <v-btn
                        v-if="!showBlacklistCreate"
                        text
                        small
                        color="error"
                        @click="startBlacklistCreate"
                      >
                        <v-icon
                          left
                          small
                        >
                          {{ icons.mdiPlus }}
                        </v-icon>
                        Criar nova
                      </v-btn>
                      <v-btn
                        v-else
                        text
                        small
                        @click="cancelBlacklistCreate"
                      >
                        <v-icon
                          left
                          small
                        >
                          {{ icons.mdiClose }}
                        </v-icon>
                        Cancelar
                      </v-btn>
                    </div>

                    <v-card
                      v-if="showBlacklistCreate"
                      outlined
                      class="mb-4 pa-4"
                    >
                      <v-text-field
                        v-model="blacklistDraft.name"
                        label="Nome da blacklist *"
                        outlined
                        dense
                        hide-details="auto"
                        :error-messages="blacklistDraftErrors.name"
                        class="mb-3"
                      />

                      <v-btn
                        outlined
                        color="primary"
                        block
                        class="mb-3"
                        @click="openCboSearchModal('blacklist')"
                      >
                        <v-icon left>
                          {{ icons.mdiMagnify }}
                        </v-icon>
                        Buscar ocupação por nome
                      </v-btn>

                      <div class="d-flex flex-wrap align-start">
                        <v-textarea
                          v-model="blacklistPasteText"
                          class="flex-grow-1 me-sm-3 mb-2"
                          outlined
                          dense
                          rows="2"
                          auto-grow
                          hide-details="auto"
                          :error-messages="blacklistPasteError"
                          placeholder="Ou cole CBOs: 241005, 241010 (Advogado, Juiz...)"
                        />
                        <v-btn
                          outlined
                          color="primary"
                          class="mb-2"
                          :disabled="!blacklistPasteText.trim()"
                          @click="validateBlacklistPaste"
                        >
                          Validar
                        </v-btn>
                      </div>

                      <div
                        v-if="blacklistDraft.cbos.length"
                        class="d-flex flex-wrap mt-2 mb-3"
                      >
                        <v-chip
                          v-for="cbo in blacklistDraft.cbos"
                          :key="`draft-${cbo.code}`"
                          class="me-2 mb-2"
                          close
                          label
                          small
                          @click:close="removeDraftBlacklistCbo(cbo.code)"
                        >
                          {{ cbo.code }} — {{ truncateLabel(cbo.name, 18) }}
                        </v-chip>
                      </div>

                      <v-btn
                        color="primary"
                        block
                        class="mb-2"
                        @click="saveAndApplyBlacklist"
                      >
                        <v-icon left>
                          {{ icons.mdiContentSaveOutline }}
                        </v-icon>
                        Salvar e aplicar blacklist
                      </v-btn>
                      <div class="text-center">
                        <v-btn
                          text
                          small
                          @click="cancelBlacklistCreate"
                        >
                          Cancelar criação
                        </v-btn>
                      </div>
                    </v-card>

                    <v-card
                      v-for="item in savedBlacklists"
                      :key="item.id"
                      outlined
                      class="blacklist-item mb-2"
                      :class="{ 'blacklist-item--active': isBlacklistActive(item.id) }"
                      @click="toggleSavedBlacklist(item)"
                    >
                      <v-list-item class="py-2">
                        <v-list-item-avatar
                          rounded
                          :class="isBlacklistActive(item.id) ? 'vol-avatar vol-avatar--error' : 'vol-avatar vol-avatar--muted'"
                        >
                          <v-icon>
                            {{ icons.mdiShieldOutline }}
                          </v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            {{ item.name }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ item.cbos.length }} CBO{{ item.cbos.length === 1 ? '' : 's' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action class="d-flex flex-row align-center">
                          <v-chip
                            v-if="isBlacklistActive(item.id)"
                            color="error"
                            small
                            label
                            class="me-2"
                          >
                            Ativa
                          </v-chip>
                          <v-btn
                            icon
                            small
                            color="error"
                            title="Excluir blacklist"
                            @click.stop="removeSavedBlacklist(item)"
                          >
                            <v-icon small>
                              {{ icons.mdiDeleteOutline }}
                            </v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-card>
                  </div>
                </div>

                <div class="d-flex justify-center mb-4">
                  <v-btn
                    outlined
                    color="primary"
                    @click="saveFiltersAsTemplate"
                  >
                    <v-icon left>
                      {{ icons.mdiBookmarkOutline }}
                    </v-icon>
                    Salvar filtros como modelo
                  </v-btn>
                </div>

                <div class="d-flex flex-wrap align-center justify-space-between">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    Voltar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    :loading="consultingVolume"
                    :disabled="consultingVolume"
                    @click="finishFilterWizard"
                  >
                    {{ flowMode === 'personalizada' ? 'Continuar' : 'Consultar volume' }}
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </template>

          <!-- Etapa 8: Pedido personalizado (descrição + cobrança) -->
          <template v-else-if="currentStep === 8">
            <v-card
              outlined
              class="mb-4"
            >
              <v-list-item class="py-3">
                <v-list-item-avatar
                  rounded
                  class="vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiAccountEdit }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold text-wrap d-flex align-center">
                    <v-icon
                      color="primary"
                      small
                      class="me-1"
                    >
                      {{ icons.mdiAutoFix }}
                    </v-icon>
                    Descreva o perfil desejado
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Nosso time analisa a descrição e devolve um orçamento com valor e prazo
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card>

            <v-card outlined>
              <v-card-text class="pt-5">
                <v-alert
                  v-if="flowMode === 'personalizada'"
                  dense
                  colored-border
                  border="left"
                  color="primary"
                  class="mb-4"
                >
                  <div class="d-flex align-start">
                    <v-icon
                      color="primary"
                      small
                      class="me-2 mt-1"
                    >
                      {{ icons.mdiInformationOutline }}
                    </v-icon>
                    <span>
                      Os filtros preenchidos nas etapas anteriores serão enviados junto com a descrição.
                    </span>
                  </div>
                </v-alert>

                <v-textarea
                  v-model="descricaoLivre"
                  label="Descrição do perfil *"
                  placeholder="Ex: mulheres de 30 a 45 anos, com renda acima de 8 mil, moradoras da zona sul de SP, que tenham WhatsApp ativo..."
                  outlined
                  rows="6"
                  auto-grow
                  counter="2000"
                  hide-details="auto"
                  class="mb-5"
                />

                <p class="subtitle-2 font-weight-medium mb-3">
                  Como deseja pagar? <span class="error--text">*</span>
                </p>

                <v-row dense>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-card
                      outlined
                      class="option-card"
                      :class="{ 'option-card--selected': modoCobranca === 'a_faturar' }"
                      @click="modoCobranca = 'a_faturar'"
                    >
                      <v-list-item class="py-3">
                        <v-list-item-avatar
                          rounded
                          :class="optionAvatarClass(modoCobranca === 'a_faturar')"
                        >
                          <v-icon>
                            {{ icons.mdiFileDocumentOutline }}
                          </v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            A faturar
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-wrap">
                            Cobrança na próxima fatura
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-card
                      outlined
                      class="option-card"
                      :class="{ 'option-card--selected': modoCobranca === 'descontar_saldo' }"
                      @click="modoCobranca = 'descontar_saldo'"
                    >
                      <v-list-item class="py-3">
                        <v-list-item-avatar
                          rounded
                          :class="optionAvatarClass(modoCobranca === 'descontar_saldo')"
                        >
                          <v-icon>
                            {{ icons.mdiWalletOutline }}
                          </v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            Descontar do saldo
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-wrap">
                            Usa o saldo disponível na conta
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap align-center justify-space-between mt-6">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="mb-2 me-2"
                    :disabled="submittingPersonalizada"
                    @click="goBack"
                  >
                    <v-icon left>
                      {{ icons.mdiArrowLeft }}
                    </v-icon>
                    {{ flowMode === 'personalizada' ? 'Voltar' : 'Cancelar' }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="mb-2 flex-grow-1"
                    style="max-width: 420px;"
                    :loading="submittingPersonalizada"
                    :disabled="submittingPersonalizada || !descricaoLivre.trim()"
                    @click="submitPedidoPersonalizado"
                  >
                    Enviar pedido
                    <v-icon right>
                      {{ icons.mdiArrowRight }}
                    </v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </template>

          <!-- Placeholder (não deve aparecer) -->
          <template v-else>
            <v-card outlined>
              <v-card-text class="py-10 text-center">
                <v-avatar
                  size="56"
                  class="mb-4 vol-avatar"
                >
                  <v-icon>
                    {{ icons.mdiAutoFix }}
                  </v-icon>
                </v-avatar>
                <p class="text-h6 font-weight-bold mb-1">
                  Etapa {{ currentStep }} em breve
                </p>
                <p class="grey--text mb-0">
                  As próximas etapas do wizard serão implementadas em seguida.
                </p>
              </v-card-text>
            </v-card>

            <div class="d-flex justify-center mt-6">
              <v-btn
                text
                color="grey darken-1"
                @click="goBack"
              >
                <v-icon left>
                  {{ icons.mdiArrowLeft }}
                </v-icon>
                Voltar
              </v-btn>
            </div>
          </template>
        </v-col>
      </v-row>
    </template>

    <!-- Tela inicial (tabs) -->
    <template v-else>
      <v-row>
        <v-col cols="12">
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            class="mb-2"
          >
            <v-tab>
              <v-icon
                left
                small
              >
                {{ icons.mdiMagnify }}
              </v-icon>
              Nova consulta
            </v-tab>
            <v-tab>
              <v-icon
                left
                small
              >
                {{ icons.mdiTextBoxOutline }}
              </v-icon>
              Lista de volumetrias criadas
            </v-tab>
            <v-tab>
              <v-icon
                left
                small
              >
                {{ icons.mdiBookmarkOutline }}
              </v-icon>
              Modelos
            </v-tab>
          </v-tabs>

          <v-tabs-items
            v-model="activeTab"
            class="volumetry-tabs-items"
          >
            <!-- Nova consulta -->
            <v-tab-item>
              <v-card
                outlined
                class="mb-4"
              >
                <v-card-text class="pt-6 text-center">
                  <div class="d-flex flex-wrap align-center justify-center mb-4">
                    <v-chip
                      outlined
                      label
                      small
                      class="ma-1 vol-chip-filter vol-chip-filter--active"
                    >
                      <v-icon
                        left
                        x-small
                      >
                        {{ icons.mdiChartBar }}
                      </v-icon>
                      {{ b2cQuotaUsed }}/{{ b2cQuotaLimit }} consultas hoje
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="primary"
                      class="ma-1"
                      @click="openTour"
                    >
                      <v-icon
                        left
                        small
                      >
                        {{ icons.mdiHelpCircleOutline }}
                      </v-icon>
                      Como usar?
                    </v-btn>
                  </div>

                  <p class="text-h6 font-weight-bold mb-1">
                    Vamos configurar sua consulta
                  </p>
                  <p class="grey--text text--darken-1 mb-6">
                    Dê um nome para identificar esta consulta depois.
                  </p>

                  <validation-observer
                    ref="observer"
                    tag="form"
                    autocomplete="off"
                    class="text-left mx-auto"
                    style="max-width: 520px;"
                    @submit.prevent="startConsultation"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="Nome da consulta"
                      rules="required|max:120"
                      vid="queryName"
                    >
                      <v-text-field
                        v-model="queryName"
                        :error-messages="errors"
                        :hide-details="errors.length === 0"
                        label="Nome da consulta *"
                        hint="Dê um nome claro para identificar depois"
                        persistent-hint
                        outlined
                        dense
                        class="mb-4"
                      />
                    </validation-provider>

                    <v-btn
                      color="primary"
                      block
                      large
                      class="mt-2"
                      type="submit"
                      @click.prevent="startConsultation"
                    >
                      <v-icon left>
                        {{ icons.mdiAutoFix }}
                      </v-icon>
                      Iniciar volumetria B2C
                    </v-btn>

                    <v-btn
                      outlined
                      color="primary"
                      block
                      class="mt-3"
                      type="button"
                      @click.prevent="startPersonalizada(false)"
                    >
                      <v-icon left>
                        {{ icons.mdiAccountEdit }}
                      </v-icon>
                      Volumetria personalizada
                    </v-btn>

                    <v-btn
                      text
                      color="primary"
                      block
                      class="mt-2"
                      type="button"
                      @click.prevent="startPersonalizada(true)"
                    >
                      <v-icon left>
                        {{ icons.mdiFileDocumentOutline }}
                      </v-icon>
                      Descrever perfil direto
                    </v-btn>

                    <p class="text-caption grey--text text-center mt-3 mb-0">
                      A volumetria B2C usa sua cota diária. As opções personalizadas geram um
                      orçamento analisado pelo nosso time.
                    </p>
                  </validation-observer>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Lista de volumetrias -->
            <v-tab-item>
              <v-card outlined>
                <v-card-title class="d-flex align-center justify-space-between flex-wrap px-4 pt-4 pb-2">
                  <span>Volumetrias criadas</span>
                  <v-btn
                    text
                    small
                    color="primary"
                    :loading="loadingFila"
                    @click="loadFilaLista"
                  >
                    <v-icon
                      left
                      small
                    >
                      {{ icons.mdiRefresh }}
                    </v-icon>
                    Atualizar
                  </v-btn>
                </v-card-title>
                <v-card-text class="px-4 pb-4">
                  <div class="d-flex flex-wrap align-center mb-4">
                    <div class="d-flex flex-wrap align-center me-4 mb-2">
                      <span class="text-caption grey--text me-2">Período:</span>
                      <v-chip
                        v-for="option in listaDiasOptions"
                        :key="`dias-${option.value}`"
                        class="me-1 vol-chip-filter"
                        :class="{ 'vol-chip-filter--active': listaUltimosDias === option.value }"
                        small
                        label
                        outlined
                        @click="listaUltimosDias = option.value"
                      >
                        {{ option.label }}
                      </v-chip>
                    </div>

                    <div class="d-flex flex-wrap align-center mb-2">
                      <span class="text-caption grey--text me-2">Status:</span>
                      <v-chip
                        v-for="option in listaStatusOptions"
                        :key="`status-${option.value}`"
                        class="me-1 vol-chip-filter"
                        :class="{ 'vol-chip-filter--active': listaStatusFilter === option.value }"
                        small
                        label
                        outlined
                        @click="listaStatusFilter = option.value"
                      >
                        {{ option.label }}
                      </v-chip>
                    </div>
                  </div>

                  <v-data-table
                    :headers="filaHeaders"
                    :items="filaListaFiltrada"
                    :loading="loadingFila"
                    :items-per-page="10"
                    class="elevation-0"
                    no-data-text="Nenhuma volumetria criada ainda."
                    loading-text="Carregando volumetrias..."
                  >
                    <template #item.tipo="{ item }">
                      <v-chip
                        x-small
                        label
                        outlined
                        class="vol-chip-tipo"
                        :class="item.tipo === 'personalizada' ? 'vol-chip-tipo--orcamento' : 'vol-chip-tipo--b2c'"
                      >
                        {{ item.tipo === 'personalizada' ? 'Orçamento' : 'B2C' }}
                      </v-chip>
                    </template>
                    <template #item.status="{ item }">
                      <v-chip
                        x-small
                        label
                        class="vol-chip-status"
                        :class="`vol-chip-status--${filaStatusColor(item.statusKey || item.status)}`"
                      >
                        {{ item.statusLabel || item.status || '—' }}
                      </v-chip>
                    </template>
                    <template #item.volume="{ item }">
                      {{ item.volumeLabel }}
                    </template>
                    <template #item.valor="{ item }">
                      {{ item.valorLabel }}
                    </template>
                    <template #item.data="{ item }">
                      {{ item.dataLabel }}
                    </template>
                    <template #item.actions="{ item }">
                      <div class="d-flex flex-wrap justify-end">
                        <v-btn
                          v-if="canAprovarOrcamento(item)"
                          x-small
                          color="success"
                          class="me-1 mb-1"
                          :loading="actionLoadingId === item.id && actionType === 'aprovar-orcamento'"
                          @click="openAprovarOrcamento(item)"
                        >
                          Aprovar
                        </v-btn>
                        <v-btn
                          v-if="canAprovarOrcamento(item)"
                          x-small
                          outlined
                          color="error"
                          class="me-1 mb-1"
                          :loading="actionLoadingId === item.id && actionType === 'recusar-orcamento'"
                          @click="openRecusarOrcamento(item)"
                        >
                          Recusar
                        </v-btn>
                        <v-btn
                          v-if="item.resultadoUrl"
                          x-small
                          outlined
                          color="primary"
                          class="me-1 mb-1"
                          :href="item.resultadoUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Resultado
                        </v-btn>
                        <v-btn
                          v-if="canExecutarB2c(item)"
                          x-small
                          outlined
                          color="primary"
                          class="me-1 mb-1"
                          :loading="actionLoadingId === item.id && actionType === 'executar'"
                          @click="executarItemB2c(item)"
                        >
                          Executar
                        </v-btn>
                        <v-btn
                          v-if="canEnriquecerLeads(item)"
                          x-small
                          color="primary"
                          class="me-1 mb-1"
                          title="Reservar leads desta volumetria"
                          @click="irParaEnriquecerLeads(item)"
                        >
                          Enriquecer leads
                        </v-btn>
                        <v-btn
                          v-if="canRepetirVolumetria(item)"
                          x-small
                          outlined
                          color="secondary"
                          class="me-1 mb-1"
                          title="Repetir esta volumetria"
                          @click="repetirVolumetria(item, 'repetir')"
                        >
                          <v-icon
                            left
                            x-small
                          >
                            {{ icons.mdiReplay }}
                          </v-icon>
                          Repetir
                        </v-btn>
                        <v-btn
                          v-if="canRepetirVolumetria(item)"
                          x-small
                          outlined
                          color="secondary"
                          class="mb-1"
                          title="Duplicar filtros desta volumetria"
                          @click="repetirVolumetria(item, 'duplicar')"
                        >
                          <v-icon
                            left
                            x-small
                          >
                            {{ icons.mdiContentCopy }}
                          </v-icon>
                          Duplicar
                        </v-btn>
                        <span
                          v-if="!canAprovarOrcamento(item) && !item.resultadoUrl && !canExecutarB2c(item) && !canEnriquecerLeads(item) && !canRepetirVolumetria(item)"
                          class="grey--text text-caption"
                        >
                          —
                        </span>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Modelos de filtros salvos -->
            <v-tab-item>
              <v-card outlined>
                <v-card-title class="d-flex align-center justify-space-between flex-wrap px-4 pt-4 pb-2">
                  <span>Modelos de filtros</span>
                  <v-btn
                    text
                    small
                    color="primary"
                    :loading="loadingModelos"
                    @click="loadModelos"
                  >
                    <v-icon
                      left
                      small
                    >
                      {{ icons.mdiRefresh }}
                    </v-icon>
                    Atualizar
                  </v-btn>
                </v-card-title>
                <v-card-text class="px-4 pb-4">
                  <v-text-field
                    v-model="modeloSearch"
                    label="Buscar modelo"
                    placeholder="Digite o nome do modelo"
                    outlined
                    dense
                    clearable
                    hide-details
                    class="mb-4"
                    :prepend-inner-icon="icons.mdiMagnify"
                  />

                  <v-progress-linear
                    v-if="loadingModelos"
                    indeterminate
                    color="primary"
                    class="mb-4"
                  />

                  <div
                    v-if="!loadingModelos && !modelosFiltrados.length"
                    class="text-center py-8"
                  >
                    <v-avatar
                      size="56"
                      class="mb-4 vol-avatar vol-avatar--muted"
                    >
                      <v-icon>
                        {{ icons.mdiBookmarkOutline }}
                      </v-icon>
                    </v-avatar>
                    <p class="font-weight-medium mb-1">
                      Nenhum modelo salvo
                    </p>
                    <p class="grey--text text-caption mb-0">
                      Na última etapa do wizard use "Salvar filtros como modelo" para reutilizar depois.
                    </p>
                  </div>

                  <v-card
                    v-for="modelo in modelosFiltrados"
                    :key="modelo.id"
                    outlined
                    class="mb-2"
                  >
                    <v-list-item class="py-2">
                      <v-list-item-avatar
                        rounded
                        class="vol-avatar"
                      >
                        <v-icon>
                          {{ icons.mdiBookmarkOutline }}
                        </v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ modelo.nome }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Modelo #{{ modelo.id }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="d-flex flex-row align-center">
                        <v-btn
                          x-small
                          color="primary"
                          class="me-2"
                          @click="carregarModelo(modelo)"
                        >
                          Carregar
                        </v-btn>
                        <v-btn
                          icon
                          small
                          color="error"
                          title="Excluir modelo"
                          @click="excluirModelo(modelo)"
                        >
                          <v-icon small>
                            {{ icons.mdiDeleteOutline }}
                          </v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </template>

    <v-dialog
      v-model="showCboSearchModal"
      max-width="720"
      scrollable
    >
      <v-card>
        <v-card-title class="align-start pb-0">
          <div class="pr-8">
            <div class="text-h6">
              Buscar Ocupações (CBO)
            </div>
            <div class="subtitle-2 grey--text font-weight-regular text-wrap">
              Digite o nome da profissão ou cargo para encontrar o código CBO
            </div>
          </div>
          <v-spacer />
          <v-btn
            icon
            @click="closeCboSearchModal"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-text-field
            v-model="cboSearchQuery"
            outlined
            dense
            hide-details
            clearable
            :loading="cboCatalogLoading"
            placeholder="Ex: médico, engenheiro, analista, pedreiro, vendedor..."
            :prepend-inner-icon="icons.mdiMagnify"
            class="mb-4"
            @input="onCboSearchInput"
            @click:clear="cboSearchQuery = ''"
          />

          <v-alert
            v-if="cboCatalogLoading"
            dense
            text
            type="info"
            class="mb-4"
          >
            Carregando catálogo completo de CBOs...
          </v-alert>

          <template v-if="!cboSearchQuery.trim()">
            <div class="text-center py-6">
              <v-avatar
                size="72"
                class="mb-4 vol-avatar"
              >
                <v-icon large>
                  {{ icons.mdiMagnify }}
                </v-icon>
              </v-avatar>
              <p class="font-weight-medium mb-1">
                Busque por qualquer profissão
              </p>
              <p class="grey--text text-caption mb-4">
                Ex: 'motorista', 'professor', 'enfermeiro', 'contador'
              </p>
              <div class="d-flex flex-wrap justify-center">
                <v-chip
                  v-for="suggestion in cboSuggestions"
                  :key="suggestion"
                  class="ma-1"
                  color="primary"
                  outlined
                  label
                  @click="applyCboSuggestion(suggestion)"
                >
                  {{ suggestion }}
                </v-chip>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-caption grey--text">
                <strong class="primary--text">{{ cboSearchOccupationCount }}</strong>
                ocupação{{ cboSearchOccupationCount === 1 ? '' : 'ões' }} encontrada{{ cboSearchOccupationCount === 1 ? '' : 's' }}
                em
                <strong class="primary--text">{{ cboSearchResults.length }}</strong>
                família{{ cboSearchResults.length === 1 ? '' : 's' }}
              </span>
              <v-chip
                v-if="cboModalSelection.length"
                color="primary"
                small
                class="vol-chip-accent"
              >
                {{ cboModalSelection.length }} selecionado{{ cboModalSelection.length === 1 ? '' : 's' }}
              </v-chip>
            </div>

            <div
              v-if="!cboSearchResults.length"
              class="text-center grey--text py-8"
            >
              Nenhuma ocupação encontrada para "{{ cboSearchQuery }}".
            </div>

            <div
              v-for="family in cboSearchResults"
              :key="family.familyCode"
              class="cbo-family-card mb-3"
            >
              <div class="d-flex align-center justify-space-between pa-3">
                <div class="d-flex align-center">
                  <v-avatar
                    size="32"
                    class="me-2 vol-avatar"
                  >
                    <v-icon small>
                      {{ icons.mdiAccountMultipleOutline }}
                    </v-icon>
                  </v-avatar>
                  <div>
                    <v-chip
                      x-small
                      color="primary"
                      class="me-1 mb-1 vol-chip-accent"
                    >
                      Família {{ family.familyCode }}
                    </v-chip>
                    <div class="font-weight-medium">
                      {{ family.familyName }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    small
                    :color="isCboFamilyFullySelected(family) ? 'primary' : undefined"
                    :outlined="!isCboFamilyFullySelected(family)"
                    class="me-1"
                    @click="toggleCboFamilySelection(family)"
                  >
                    <v-icon
                      v-if="isCboFamilyFullySelected(family)"
                      left
                      small
                    >
                      {{ icons.mdiCheck }}
                    </v-icon>
                    Família
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="toggleCboFamilyExpanded(family.familyCode)"
                  >
                    <v-icon small>
                      {{ isCboFamilyExpanded(family.familyCode) ? icons.mdiChevronUp : icons.mdiChevronDown }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <div
                v-if="isCboFamilyExpanded(family.familyCode)"
                class="px-2 pb-2"
              >
                <button
                  v-for="occupation in family.occupations"
                  :key="occupation.code"
                  type="button"
                  class="cbo-occupation-row"
                  :class="{ 'cbo-occupation-row--selected': isCboModalSelected(occupation.code) }"
                  @click="toggleCboModalOccupation(occupation)"
                >
                  <v-icon
                    small
                    class="me-2"
                    :class="isCboModalSelected(occupation.code) ? 'vol-icon-accent' : 'vol-icon-muted'"
                  >
                    {{ isCboModalSelected(occupation.code) ? icons.mdiCheckCircle : icons.mdiCheckboxBlankCircleOutline }}
                  </v-icon>
                  <span class="flex-grow-1 text-left">{{ occupation.name }}</span>
                  <span class="text-caption grey--text font-weight-medium">{{ occupation.code }}</span>
                </button>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            text
            @click="closeCboSearchModal"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!cboModalSelection.length"
            @click="addSelectedCbosFromModal"
          >
            <v-icon left>
              {{ icons.mdiPlus }}
            </v-icon>
            Adicionar selecionados{{ cboModalSelection.length ? ` (${cboModalSelection.length})` : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Gate de cota diária -->
    <v-dialog
      v-model="cotaDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title class="align-start">
          <v-avatar
            size="36"
            class="me-3"
            :class="cotaInfo && cotaInfo.tipoCobranca === 'BLOQUEADO'
              ? 'vol-avatar vol-avatar--error'
              : 'vol-avatar'"
          >
            <v-icon small>
              {{ icons.mdiInformationOutline }}
            </v-icon>
          </v-avatar>
          <div>
            <div class="text-h6">
              {{ cotaDialogTitle }}
            </div>
            <div class="text-caption grey--text font-weight-regular text-wrap">
              {{ cotaDialogSubtitle }}
            </div>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-progress-linear
            v-if="cotaConfirming"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <template v-if="cotaInfo">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="grey--text text--darken-1">Consultas usadas hoje</span>
              <strong>{{ cotaInfo.usadasHoje }} / {{ cotaInfo.limiteDiario }}</strong>
            </div>
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="grey--text text--darken-1">Restantes</span>
              <strong :class="cotaInfo.restantesHoje > 0 ? 'success--text' : 'error--text'">
                {{ cotaInfo.restantesHoje }}
              </strong>
            </div>

            <v-progress-linear
              :value="cotaInfo.limiteDiario ? (cotaInfo.usadasHoje / cotaInfo.limiteDiario) * 100 : 0"
              color="primary"
              height="6"
              rounded
              class="mb-4"
            />

            <v-alert
              v-if="cotaInfo.tipoCobranca === 'USAR_SALDO'"
              dense
              colored-border
              border="left"
              color="warning"
              class="mb-0"
            >
              <p class="mb-1 font-weight-medium">
                Sua cota gratuita de hoje acabou.
              </p>
              <p class="mb-1 text-caption">
                Esta consulta custará {{ formatCurrencyBRL(cotaInfo.valorConsultaExtra) }} e será
                debitada do seu saldo.
              </p>
              <p class="mb-0 text-caption">
                Saldo atual: <strong>{{ formatCurrencyBRL(cotaInfo.saldoAtual) }}</strong>
              </p>
            </v-alert>

            <v-alert
              v-else-if="cotaInfo.tipoCobranca === 'BLOQUEADO'"
              dense
              colored-border
              border="left"
              color="error"
              class="mb-0"
            >
              {{ cotaInfo.mensagemModal || 'Saldo insuficiente para realizar novas consultas hoje.' }}
            </v-alert>

            <p
              v-else-if="cotaInfo.mensagemModal"
              class="grey--text text--darken-1 mb-0"
            >
              {{ cotaInfo.mensagemModal }}
            </p>
          </template>

          <p
            v-else-if="!cotaConfirming"
            class="grey--text mb-0"
          >
            Não foi possível carregar sua cota diária. Você pode prosseguir e a validação
            será feita no envio.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            :disabled="cotaConfirming"
            @click="closeCotaDialog"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="cotaConfirming || (cotaInfo && !cotaInfo.podeProsseguir)"
            @click="confirmCotaAndStart"
          >
            Confirmar e iniciar
            <v-icon right>
              {{ icons.mdiArrowRight }}
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tour "Como usar?" -->
    <v-dialog
      v-model="tourDialog"
      max-width="560"
      scrollable
    >
      <v-card>
        <v-card-title class="align-start pb-2">
          <v-avatar
            size="36"
            class="me-3 vol-avatar"
          >
            <v-icon small>
              {{ icons.mdiHelpCircleOutline }}
            </v-icon>
          </v-avatar>
          <div class="pr-8">
            <div class="text-h6">
              Como usar a volumetria
            </div>
            <div class="text-caption grey--text font-weight-regular text-wrap">
              7 passos rápidos para montar sua primeira consulta
            </div>
          </div>
          <v-spacer />
          <v-btn
            icon
            @click="closeTour"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div
            v-for="(tip, index) in tourSteps"
            :key="`tip-${index}`"
            class="d-flex align-start mb-4"
          >
            <v-avatar
              size="28"
              class="me-3 vol-avatar"
            >
              <span class="text-caption font-weight-bold">
                {{ index + 1 }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-bold">
                {{ tip.title }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                {{ tip.text }}
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="primary"
            @click="closeTour"
          >
            Entendi, fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Cliente aprova / recusa orçamento personalizado -->
    <v-dialog
      v-model="orcamentoDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ orcamentoDialogMode === 'aprovar' ? 'Confirmar orçamento' : 'Recusar orçamento' }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-2">
            <strong>{{ (orcamentoSelected && orcamentoSelected.nome) || '—' }}</strong>
          </p>
          <p
            v-if="orcamentoSelected && orcamentoSelected.valorLabel && orcamentoSelected.valorLabel !== '—'"
            class="mb-3 primary--text font-weight-bold text-h6"
          >
            {{ orcamentoSelected.valorLabel }}
          </p>
          <template v-if="orcamentoDialogMode === 'aprovar'">
            <p
              v-if="orcamentoSelected && orcamentoSelected.observacoesAdmin"
              class="mb-3 text-body-2"
            >
              {{ orcamentoSelected.observacoesAdmin }}
            </p>
            <p
              v-if="orcamentoSelected && orcamentoSelected.prazo"
              class="mb-3 text-caption grey--text text--darken-1"
            >
              Prazo estimado: {{ orcamentoSelected.prazo }}
            </p>
            <p
              v-if="orcamentoSelected && orcamentoSelected.modoCobranca === 'descontar_saldo'"
              class="mb-2 warning--text text--darken-2"
            >
              Ao aprovar, o valor do orçamento será debitado do seu saldo Ligdata.
            </p>
            <p
              v-else-if="orcamentoSelected && orcamentoSelected.modoCobranca === 'a_faturar'"
              class="mb-2 grey--text text--darken-1"
            >
              Este orçamento será cobrado na próxima fatura (sem débito imediato do saldo).
            </p>
            <p class="mb-2 text-caption warning--text text--darken-2">
              Este orçamento refere-se apenas ao serviço de volumetria. A aquisição dos dados é cobrada separadamente.
            </p>
            <p class="grey--text mb-0">
              Ao aprovar, o orçamento será contratado conforme as condições enviadas.
            </p>
          </template>
          <template v-else>
            <p class="text-caption grey--text mb-2">
              Motivos frequentes
            </p>
            <div class="d-flex flex-wrap mb-2">
              <v-chip
                v-for="motivo in orcamentoRejectReasons"
                :key="motivo"
                class="me-2 mb-2"
                small
                label
                :color="orcamentoMotivo === motivo ? 'error' : undefined"
                :outlined="orcamentoMotivo !== motivo"
                @click="orcamentoMotivo = motivo"
              >
                {{ motivo }}
              </v-chip>
            </div>
            <v-textarea
              v-model="orcamentoMotivo"
              label="Motivo da recusa"
              outlined
              dense
              rows="3"
              auto-grow
              hide-details="auto"
              class="mt-2"
              placeholder="Ou descreva com suas palavras..."
            />
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            :disabled="orcamentoSaving"
            @click="closeOrcamentoDialog"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="orcamentoDialogMode === 'aprovar'"
            color="success"
            :loading="orcamentoSaving"
            @click="confirmarAprovarOrcamento"
          >
            Aprovar e contratar
          </v-btn>
          <v-btn
            v-else
            color="error"
            :loading="orcamentoSaving"
            :disabled="!String(orcamentoMotivo || '').trim()"
            @click="confirmarRecusarOrcamento"
          >
            Confirmar recusa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  atualizarPersonalizadaVolumetria,
  enfileirarVolumetria,
  criarPersonalizadaVolumetria,
  excluirBlacklistCbo,
  excluirModeloVolumetria,
  executarPedidoFilaVolumetria,
  getCotaDiaria,
  getEstadosVolumetria,
  getFilaVolumetria,
  getProfissoesVolumetria,
  listarBlacklistsCbo,
  listarModelosVolumetria,
  listarPersonalizadasVolumetria,
  salvarBlacklistCbo,
  salvarModeloVolumetria,
} from '@/services/volumetryB2c'
import {
  buildEnfileirarVolumetriaBody,
  buildPersonalizadaVolumetriaBody,
  normalizeBlacklistItem,
  normalizeCotaDiaria,
  normalizeEstado,
  normalizeModeloItem,
  normalizeCboApiRows,
  normalizeProfissoesToCatalog,
} from '@/utils/volumetriaMappers'
import {
  getBairrosPorCidadeProspeccao,
  getBairrosPorCodigoIbgeProspeccao,
  getCbosProspeccao,
} from '@/services/prospeccao'
import {
  mdiAccountEdit,
  mdiAccountGroupOutline,
  mdiAccountMultipleOutline,
  mdiArrowLeft,
  mdiArrowRight,
  mdiAutoFix,
  mdiBookmarkOutline,
  mdiCancel,
  mdiCellphone,
  mdiChartBar,
  mdiChartLine,
  mdiCheck,
  mdiCheckCircle,
  mdiCheckboxBlankCircleOutline,
  mdiChevronDown,
  mdiChevronRight,
  mdiChevronUp,
  mdiClose,
  mdiContentCopy,
  mdiContentSaveOutline,
  mdiDeleteOutline,
  mdiEmailOutline,
  mdiFileDocumentOutline,
  mdiHelpCircleOutline,
  mdiHumanFemale,
  mdiHumanMale,
  mdiInformationOutline,
  mdiMagnify,
  mdiMapMarkerOutline,
  mdiPartyPopper,
  mdiPlus,
  mdiRefresh,
  mdiReplay,
  mdiShieldOutline,
  mdiTextBoxOutline,
  mdiWalletOutline,
  mdiWhatsapp,
} from '@mdi/js'

const TOTAL_STEPS = 7
const PERSONALIZADA_STEP = 8
const SALARIO_MINIMO = 1412
const TOUR_STORAGE_KEY = 'ligdata_volumetria_tour_concluido'
const POLL_INTERVAL_MS = 15000
const POLLING_STATUS_KEYS = ['aguardando', 'em_execucao', 'aguardando_orcamento']

const LISTA_DIAS_OPTIONS = [
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 45, label: '45 dias' },
  { value: 60, label: '60 dias' },
]

const LISTA_STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'na_fila', label: 'Na fila / em andamento' },
  { value: 'concluidas', label: 'Concluídas' },
]

const TOUR_STEPS = [
  {
    title: 'Dê um nome à consulta',
    text: 'O nome ajuda a identificar a volumetria depois, na aba "Lista de volumetrias criadas".',
  },
  {
    title: 'Escolha o tipo de pedido',
    text: 'Volumetria B2C usa sua cota diária. A personalizada vira um orçamento analisado pelo nosso time.',
  },
  {
    title: 'Confirme sua cota do dia',
    text: 'Antes de iniciar a B2C mostramos quantas consultas você já usou e se haverá cobrança de saldo.',
  },
  {
    title: 'Percorra as 7 etapas de filtros',
    text: 'Sexo, idade, renda, localização, canais de contato, profissões (CBO) e blacklist de ocupações.',
  },
  {
    title: 'Salve filtros como modelo',
    text: 'Na última etapa você pode salvar a combinação de filtros e reutilizá-la na aba "Modelos".',
  },
  {
    title: 'Acompanhe na lista',
    text: 'A lista atualiza sozinha enquanto houver pedidos na fila ou aguardando orçamento.',
  },
  {
    title: 'Repita ou duplique',
    text: 'Use "Repetir" e "Duplicar" para criar uma nova consulta a partir de uma volumetria já existente.',
  },
]

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

// Zonas/regiões pré-definidas (atalho local, não vem de API)
const ZONE_REGIONS_MOCK = [
  {
    id: 'sao_paulo_sp',
    title: 'São Paulo - SP',
    uf: 'SP',
    zones: [
      {
        id: 'zona_leste_sp',
        title: 'Zona Leste (SP)',
        neighborhoods: [
          'Água Rasa', 'Aricanduva', 'Artur Alvim', 'Belém', 'Cangaíba', 'Carrão',
          'Ermelino Matarazzo', 'Itaim Paulista', 'Itaquera', 'Mooca', 'Penha',
          'São Mateus', 'São Miguel Paulista', 'Sapopemba', 'Tatuapé', 'Vila Formosa',
        ],
      },
      {
        id: 'zona_sul_sp',
        title: 'Zona Sul (SP)',
        neighborhoods: [
          'Brooklin', 'Campo Belo', 'Campo Limpo', 'Capão Redondo', 'Cidade Ademar',
          'Ipiranga', 'Jabaquara', 'Moema', 'Pedreira', 'Sacomã', 'Santo Amaro',
          'Saúde', 'Socorro', 'Vila Mariana',
        ],
      },
      {
        id: 'zona_norte_sp',
        title: 'Zona Norte (SP)',
        neighborhoods: [
          'Casa Verde', 'Freguesia do Ó', 'Jaçanã', 'Limão', 'Mandaqui',
          'Santana', 'Tremembé', 'Tucuruvi', 'Vila Guilherme', 'Vila Maria',
          'Vila Medeiros',
        ],
      },
      {
        id: 'zona_oeste_sp',
        title: 'Zona Oeste (SP)',
        neighborhoods: [
          'Alto de Pinheiros', 'Butantã', 'Jaguaré', 'Lapa', 'Perdizes',
          'Pinheiros', 'Raposo Tavares', 'Rio Pequeno', 'Vila Leopoldina', 'Vila Sônia',
        ],
      },
      {
        id: 'centro_sp',
        title: 'Centro (SP)',
        neighborhoods: [
          'Bela Vista', 'Bom Retiro', 'Brás', 'Cambuci', 'Consolação',
          'Liberdade', 'República', 'Santa Cecília', 'Sé',
        ],
      },
    ],
  },
  {
    id: 'grande_sp',
    title: 'Grande São Paulo',
    uf: 'SP',
    zones: [
      {
        id: 'abc_sp',
        title: 'ABC Paulista',
        neighborhoods: [
          'Santo André', 'São Bernardo do Campo', 'São Caetano do Sul',
          'Diadema', 'Mauá', 'Ribeirão Pires', 'Rio Grande da Serra',
        ],
      },
      {
        id: 'guarulhos_sp',
        title: 'Guarulhos e região',
        neighborhoods: [
          'Guarulhos', 'Arujá', 'Santa Isabel', 'Itaquaquecetuba',
        ],
      },
      {
        id: 'osasco_sp',
        title: 'Osasco e região',
        neighborhoods: [
          'Osasco', 'Barueri', 'Carapicuíba', 'Jandira', 'Itapevi', 'Santana de Parnaíba',
        ],
      },
    ],
  },
]

function normalizarTextoBusca(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function mapIbgeMunicipio(m, uf) {
  const name = m.nome || m.name || ''
  const ibge = m.id
  return {
    id: String(ibge),
    label: `${name} - ${uf}`,
    name,
    uf,
    codigoIBGE: ibge,
    ibge,
  }
}

const CBO_CATALOG_MOCK = [
  {
    familyCode: '2253',
    familyName: 'Médicos',
    occupations: [
      { code: '225355', name: 'Médico radiologista intervencionista' },
      { code: '225125', name: 'Médico clínico' },
      { code: '225142', name: 'Médico da estratégia de saúde da família' },
    ],
  },
  {
    familyCode: '2142',
    familyName: 'Engenheiros civis e afins',
    occupations: [
      { code: '214205', name: 'Engenheiro civil' },
      { code: '214210', name: 'Engenheiro de edificações' },
    ],
  },
  {
    familyCode: '2410',
    familyName: 'Advogados',
    occupations: [
      { code: '241005', name: 'Advogado' },
      { code: '241010', name: 'Advogado de empresa' },
      { code: '241015', name: 'Advogado (direito civil)' },
      { code: '241020', name: 'Advogado (direito público)' },
      { code: '241025', name: 'Advogado (direito penal)' },
      { code: '241030', name: 'Advogado (áreas especiais)' },
      { code: '241035', name: 'Advogado (direito do trabalho)' },
    ],
  },
  {
    familyCode: '2422',
    familyName: 'Promotores de justiça',
    occupations: [
      { code: '242235', name: 'Promotor de justiça' },
    ],
  },
  {
    familyCode: '2521',
    familyName: 'Analistas administrativos',
    occupations: [
      { code: '252105', name: 'Administrador' },
      { code: '252205', name: 'Analista de sistemas' },
    ],
  },
  {
    familyCode: '2311',
    familyName: 'Professores do ensino fundamental',
    occupations: [
      { code: '231105', name: 'Professor de nível médio no ensino fundamental' },
      { code: '231205', name: 'Professor de nível médio no ensino profissionalizante' },
    ],
  },
  {
    familyCode: '3541',
    familyName: 'Vendedores e representantes',
    occupations: [
      { code: '354125', name: 'Vendedor em comércio atacadista' },
      { code: '354130', name: 'Representante comercial autônomo' },
    ],
  },
  {
    familyCode: '7823',
    familyName: 'Motoristas de ônibus urbanos e metropolitanos',
    occupations: [
      { code: '782305', name: 'Motorista de ônibus urbano' },
      { code: '782310', name: 'Motorista de ônibus rodoviário' },
    ],
  },
  {
    familyCode: '2235',
    familyName: 'Enfermeiros',
    occupations: [
      { code: '223505', name: 'Enfermeiro' },
      { code: '223565', name: 'Enfermeiro da estratégia de saúde da família' },
    ],
  },
  {
    familyCode: '2522',
    familyName: 'Contadores',
    occupations: [
      { code: '252210', name: 'Contador' },
    ],
  },
]

const CBO_SUGGESTIONS = ['médico', 'advogado', 'engenheiro', 'analista', 'professor', 'vendedor']

const createSavedBlacklistsMock = () => ([
  {
    id: 'bl-advogado',
    name: 'advogado',
    cbos: [
      { code: '241005', name: 'Advogado' },
      { code: '241010', name: 'Advogado de empresa' },
      { code: '241015', name: 'Advogado (direito civil)' },
      { code: '241020', name: 'Advogado (direito público)' },
      { code: '241025', name: 'Advogado (direito penal)' },
      { code: '241030', name: 'Advogado (áreas especiais)' },
      { code: '241035', name: 'Advogado (direito do trabalho)' },
    ],
  },
  {
    id: 'bl-promotor',
    name: 'promotor',
    cbos: [
      { code: '242235', name: 'Promotor de justiça' },
    ],
  },
])

const createDefaultFilters = () => ({
  genero: null,
  idadeMin: 18,
  idadeMax: 60,
  rendaPreset: null,
  rendaMin: null,
  rendaMax: null,
  ufs: [],
  cidades: [],
  bairros: [],
  canais: [],
  cbos: [],
  cbosExcluidos: [],
  activeBlacklistIds: [],
})

export default {
  setup() {
    const activeTab = ref(0)
    const queryName = ref('')
    const wizardActive = ref(false)
    const currentStep = ref(1)
    const flowMode = ref('b2c')
    const filters = ref(createDefaultFilters())
    const ageErrors = ref({ min: [], max: [] })
    const locationErrors = ref({ ufs: [] })
    const channelErrors = ref([])
    const incomeErrors = ref([])
    const citySearch = ref('')
    const citiesLoading = ref(false)
    const citiesCache = ref({})
    const bairroSearch = ref('')
    const bairroSuggestions = ref([])
    const bairrosLoading = ref(false)
    const bairroDraft = ref(null)
    const bairrosCache = ref({})
    const zonePanelOpen = ref(false)
    const expandedRegions = ref(['sao_paulo_sp'])
    const showCboSearchModal = ref(false)
    const cboSearchQuery = ref('')
    const cboModalSelection = ref([])
    const cboExpandedFamilies = ref([])
    const cboPasteText = ref('')
    const cboPasteError = ref('')
    const b2cQuotaUsed = ref(0)
    const b2cQuotaLimit = ref(6)
    const consultingVolume = ref(false)
    const estadosByUf = ref({})
    const cboCatalog = ref([...CBO_CATALOG_MOCK])
    const cboApiRows = ref([])
    const cboCatalogLoading = ref(false)
    const cboCatalogLoaded = ref(false)
    const blacklistPanelOpen = ref(false)
    const showBlacklistCreate = ref(false)
    const blacklistDraft = ref({ name: '', cbos: [] })
    const blacklistDraftErrors = ref({ name: [] })
    const blacklistPasteText = ref('')
    const blacklistPasteError = ref('')
    const cboSearchTarget = ref('include')
    const savedBlacklists = ref(createSavedBlacklistsMock())
    const filaLista = ref([])
    const loadingFila = ref(false)
    const actionLoadingId = ref(null)
    const actionType = ref('')

    // Cliente aprova / recusa orçamento personalizado
    const orcamentoDialog = ref(false)
    const orcamentoDialogMode = ref('aprovar')
    const orcamentoSelected = ref(null)
    const orcamentoMotivo = ref('')
    const orcamentoSaving = ref(false)
    const orcamentoRejectReasons = [
      'Valor acima do esperado',
      'Prazo muito longo',
      'Não preciso mais',
      'Vou avaliar outras opções',
    ]

    // Gate de cota diária (modal antes de iniciar a B2C)
    const cotaDialog = ref(false)
    const cotaInfo = ref(null)
    const cotaConfirming = ref(false)
    const pendingStartAction = ref(null)
    const pendingFiltros = ref(null)

    // Tela de sucesso pós-envio
    const successScreen = ref(null)

    // Pedido personalizado (etapa 8)
    const descricaoLivre = ref('')
    const modoCobranca = ref('a_faturar')
    const submittingPersonalizada = ref(false)

    // Modelos salvos
    const modelosLista = ref([])
    const loadingModelos = ref(false)
    const modeloSearch = ref('')

    // Filtros da lista + polling
    const listaUltimosDias = ref(60)
    const listaStatusFilter = ref('todos')

    // Tour "Como usar?"
    const tourDialog = ref(false)
    const tourStep = ref(1)

    const filaHeaders = [
      { text: 'ID', value: 'id', width: '90px' },
      { text: 'Tipo', value: 'tipo', width: '110px' },
      { text: 'Nome', value: 'nome' },
      { text: 'Status', value: 'status' },
      { text: 'Volume', value: 'volume' },
      { text: 'Valor', value: 'valor' },
      { text: 'Data', value: 'data' },
      { text: 'Ações', value: 'actions', sortable: false, align: 'end' },
    ]

    const genderOptions = [
      {
        value: 'Masculino',
        title: 'Masculino',
        subtitle: 'Apenas homens',
        icon: mdiHumanMale,
      },
      {
        value: 'Feminino',
        title: 'Feminino',
        subtitle: 'Apenas mulheres',
        icon: mdiHumanFemale,
      },
      {
        value: 'Ambos',
        title: 'Ambos',
        subtitle: 'Sem filtro de sexo',
        icon: mdiAccountGroupOutline,
      },
    ]

    const ageShortcuts = [
      { label: '18-25', min: 18, max: 25 },
      { label: '25-35', min: 25, max: 35 },
      { label: '35-50', min: 35, max: 50 },
      { label: '50-65', min: 50, max: 65 },
      { label: '65+', min: 65, max: null },
      { label: 'TODOS', min: null, max: null },
    ]

    const formatCurrency = value => `R$ ${value.toLocaleString('pt-BR')}`

    const incomeOptions = [
      {
        value: 'ate_2',
        title: 'Até 2 salários mínimos',
        subtitle: `Até ~${formatCurrency(SALARIO_MINIMO * 2)}`,
        icon: mdiWalletOutline,
        min: null,
        max: SALARIO_MINIMO * 2,
      },
      {
        value: '2_5',
        title: '2 a 5 salários mínimos',
        subtitle: `${formatCurrency(SALARIO_MINIMO * 2)} - ${formatCurrency(SALARIO_MINIMO * 5)}`,
        icon: mdiWalletOutline,
        min: SALARIO_MINIMO * 2,
        max: SALARIO_MINIMO * 5,
      },
      {
        value: '5_10',
        title: '5 a 10 salários mínimos',
        subtitle: `${formatCurrency(SALARIO_MINIMO * 5)} - ${formatCurrency(SALARIO_MINIMO * 10)}`,
        icon: mdiWalletOutline,
        min: SALARIO_MINIMO * 5,
        max: SALARIO_MINIMO * 10,
      },
      {
        value: '10_20',
        title: '10 a 20 salários mínimos',
        subtitle: `${formatCurrency(SALARIO_MINIMO * 10)} - ${formatCurrency(SALARIO_MINIMO * 20)}`,
        icon: mdiWalletOutline,
        min: SALARIO_MINIMO * 10,
        max: SALARIO_MINIMO * 20,
      },
      {
        value: 'acima_20',
        title: 'Acima de 20 salários',
        subtitle: `Acima de ${formatCurrency(SALARIO_MINIMO * 20)}`,
        icon: mdiChartLine,
        min: SALARIO_MINIMO * 20,
        max: null,
      },
      {
        value: 'qualquer',
        title: 'Qualquer renda',
        subtitle: 'Sem filtro de renda',
        icon: mdiWalletOutline,
        min: null,
        max: null,
      },
      {
        value: 'personalizado',
        title: 'Personalizado',
        subtitle: 'Defina uma faixa específica',
        icon: mdiChevronDown,
        min: null,
        max: null,
      },
    ]

    const channelOptions = [
      {
        value: 'celular',
        title: 'Celular',
        subtitle: 'Qualquer número de celular',
        icon: mdiCellphone,
      },
      {
        value: 'whatsapp',
        title: 'Celular com WhatsApp',
        subtitle: 'Celular ativo no WhatsApp',
        icon: mdiWhatsapp,
      },
      {
        value: 'email',
        title: 'E-mail',
        subtitle: 'Endereço de e-mail válido',
        icon: mdiEmailOutline,
      },
    ]

    const neighborhoodModeActive = computed(() => filters.value.bairros.length > 0)

    const availableZoneRegions = computed(() => (
      ZONE_REGIONS_MOCK.filter(region => filters.value.ufs.includes(region.uf))
    ))

    const citySearchItems = computed(() => {
      const items = []
      filters.value.ufs.forEach(uf => {
        const fromCache = citiesCache.value[uf]
        if (fromCache?.length) items.push(...fromCache)
      })
      return items
    })

    const cboSearchResults = computed(() => {
      const query = normalizarTextoBusca(cboSearchQuery.value)
      if (!query) return []

      return cboCatalog.value
        .map(family => {
          const familyNameNorm = normalizarTextoBusca(family.familyName)
          const familyCodeNorm = String(family.familyCode || '')
          const occupations = family.occupations.filter(item => (
            normalizarTextoBusca(item.name).includes(query)
            || String(item.code).includes(query)
            || familyNameNorm.includes(query)
            || familyCodeNorm.includes(query)
          ))

          if (!occupations.length) return null

          return {
            ...family,
            occupations,
          }
        })
        .filter(Boolean)
    })

    const cboSearchOccupationCount = computed(() => (
      cboSearchResults.value.reduce((total, family) => total + family.occupations.length, 0)
    ))

    const totalSteps = computed(() => (
      flowMode.value === 'b2c' ? TOTAL_STEPS : PERSONALIZADA_STEP
    ))

    const progressPercent = computed(() => {
      const total = totalSteps.value || TOTAL_STEPS
      return Math.min(100, Math.round((currentStep.value / total) * 100))
    })

    const isPersonalizadaStep = computed(() => currentStep.value === PERSONALIZADA_STEP)

    const cotaDialogTitle = computed(() => {
      const tipo = cotaInfo.value ? cotaInfo.value.tipoCobranca : 'COTA_DISPONIVEL'
      if (tipo === 'USAR_SALDO') return 'Cota diária esgotada'
      if (tipo === 'BLOQUEADO') return 'Consulta bloqueada'
      return 'Cota disponível'
    })

    const cotaDialogSubtitle = computed(() => {
      const tipo = cotaInfo.value ? cotaInfo.value.tipoCobranca : 'COTA_DISPONIVEL'
      if (tipo === 'USAR_SALDO') return 'Esta consulta será debitada do seu saldo.'
      if (tipo === 'BLOQUEADO') return 'Não é possível iniciar uma nova consulta agora.'
      return 'Você ainda tem consultas gratuitas hoje.'
    })

    const filaListaFiltrada = computed(() => {
      const list = Array.isArray(filaLista.value) ? filaLista.value : []
      if (listaStatusFilter.value === 'na_fila') {
        return list.filter(item => POLLING_STATUS_KEYS.includes(item.statusKey))
      }
      if (listaStatusFilter.value === 'concluidas') {
        return list.filter(item => item.statusKey === 'concluido' || item.statusKey === 'aprovado')
      }
      return list
    })

    const modelosFiltrados = computed(() => {
      const query = normalizarTextoBusca(modeloSearch.value)
      const list = Array.isArray(modelosLista.value) ? modelosLista.value : []
      if (!query) return list
      return list.filter(item => normalizarTextoBusca(item.nome).indexOf(query) > -1)
    })

    return {
      activeTab,
      queryName,
      wizardActive,
      currentStep,
      flowMode,
      filters,
      ageErrors,
      locationErrors,
      channelErrors,
      incomeErrors,
      citySearch,
      citiesLoading,
      citiesCache,
      bairroSearch,
      bairroSuggestions,
      bairrosLoading,
      bairroDraft,
      bairrosCache,
      zonePanelOpen,
      expandedRegions,
      showCboSearchModal,
      cboSearchQuery,
      cboModalSelection,
      cboExpandedFamilies,
      cboPasteText,
      cboPasteError,
      b2cQuotaUsed,
      b2cQuotaLimit,
      consultingVolume,
      estadosByUf,
      cboCatalog,
      cboApiRows,
      cboCatalogLoading,
      cboCatalogLoaded,
      blacklistPanelOpen,
      showBlacklistCreate,
      blacklistDraft,
      blacklistDraftErrors,
      blacklistPasteText,
      blacklistPasteError,
      cboSearchTarget,
      savedBlacklists,
      filaLista,
      loadingFila,
      actionLoadingId,
      actionType,
      orcamentoDialog,
      orcamentoDialogMode,
      orcamentoSelected,
      orcamentoMotivo,
      orcamentoSaving,
      orcamentoRejectReasons,
      cotaDialog,
      cotaInfo,
      cotaConfirming,
      pendingStartAction,
      pendingFiltros,
      successScreen,
      descricaoLivre,
      modoCobranca,
      submittingPersonalizada,
      modelosLista,
      loadingModelos,
      modeloSearch,
      listaUltimosDias,
      listaStatusFilter,
      tourDialog,
      tourStep,
      filaHeaders,
      cboSuggestions: CBO_SUGGESTIONS,
      cboSearchResults,
      cboSearchOccupationCount,
      neighborhoodModeActive,
      availableZoneRegions,
      citySearchItems,
      genderOptions,
      ageShortcuts,
      incomeOptions,
      channelOptions,
      brazilianStates: BRAZILIAN_STATES,
      totalSteps,
      progressPercent,
      isPersonalizadaStep,
      cotaDialogTitle,
      cotaDialogSubtitle,
      filaListaFiltrada,
      modelosFiltrados,
      listaDiasOptions: LISTA_DIAS_OPTIONS,
      listaStatusOptions: LISTA_STATUS_OPTIONS,
      tourSteps: TOUR_STEPS,
      icons: {
        mdiAccountEdit,
        mdiAccountGroupOutline,
        mdiAccountMultipleOutline,
        mdiArrowLeft,
        mdiArrowRight,
        mdiAutoFix,
        mdiBookmarkOutline,
        mdiCancel,
        mdiCellphone,
        mdiChartBar,
        mdiChartLine,
        mdiCheck,
        mdiCheckCircle,
        mdiCheckboxBlankCircleOutline,
        mdiChevronDown,
        mdiChevronRight,
        mdiChevronUp,
        mdiClose,
        mdiContentCopy,
        mdiContentSaveOutline,
        mdiDeleteOutline,
        mdiEmailOutline,
        mdiFileDocumentOutline,
        mdiHelpCircleOutline,
        mdiHumanFemale,
        mdiHumanMale,
        mdiInformationOutline,
        mdiMagnify,
        mdiMapMarkerOutline,
        mdiPartyPopper,
        mdiPlus,
        mdiRefresh,
        mdiReplay,
        mdiShieldOutline,
        mdiTextBoxOutline,
        mdiWalletOutline,
        mdiWhatsapp,
      },
    }
  },

  async mounted() {
    this.maybeOpenTourOnFirstVisit()
    if (this.$route.query?.tab === 'lista') {
      this.activeTab = 1
    }
    await Promise.all([
      this.loadCotaDiaria(),
      this.loadBlacklists(),
      this.loadEstados(),
      this.loadProfissoes(),
      this.loadModelos(),
    ])
  },

  beforeDestroy() {
    this.stopFilaPolling()
    if (this._bairroSearchTimer) clearTimeout(this._bairroSearchTimer)
    if (this._tourTimer) clearTimeout(this._tourTimer)
  },

  watch: {
    activeTab(tab) {
      if (tab === 1) {
        this.loadFilaLista()
      } else {
        this.stopFilaPolling()
      }
      if (tab === 2) this.loadModelos()
    },
    listaUltimosDias() {
      if (this.activeTab === 1) this.loadFilaLista()
    },
    'filters.cidades'(cities) {
      const list = Array.isArray(cities) ? cities : []
      if (list.length > 1) {
        this.filters.bairros = []
        this.bairroSuggestions = []
        this.bairroSearch = ''
        this.bairroDraft = null
      }
      if (list.length !== 1) {
        this.bairroSuggestions = []
        this.bairroSearch = ''
        this.bairroDraft = null
      } else {
        this.ensureBairrosLoaded(list[0])
      }
    },
  },

  methods: {
    optionAvatarClass(selected) {
      return selected ? 'vol-avatar' : 'vol-avatar vol-avatar--muted'
    },

    normalizeFilaItem(row) {
      if (!row || typeof row !== 'object') return null
      const id = row.Id ?? row.id ?? row.IdFila ?? row.idFila
      if (id == null || String(id).trim() === '') return null

      const volume = row.ResultadoTotal
        ?? row.resultadoTotal
        ?? row.VolumeTotal
        ?? row.volumeTotal
        ?? row.TotalEstimado
        ?? row.totalEstimado
        ?? row.VolumeEstimado
        ?? row.volumeEstimado
        ?? row.TotalRegistros
        ?? row.totalRegistros
        ?? row.Quantidade
        ?? row.quantidade
      const dataRaw = row.DataInclusao
        ?? row.dataInclusao
        ?? row.DtCriacao
        ?? row.dtCriacao
        ?? row.DtSolicitacao
        ?? row.dtSolicitacao
        ?? row.Data
        ?? row.data

      let dataLabel = '—'
      if (dataRaw) {
        const d = new Date(dataRaw)
        dataLabel = Number.isNaN(d.getTime())
          ? String(dataRaw)
          : d.toLocaleString('pt-BR')
      }

      const statusRaw = row.Status ?? row.status ?? row.StatusDescricao ?? row.statusDescricao ?? '—'
      const statusKey = this.normalizeStatusKey(statusRaw)

      return {
        id,
        tipo: 'b2c',
        nome: row.NomeConsulta ?? row.nomeConsulta ?? row.Nome ?? row.nome ?? '—',
        status: statusRaw,
        statusKey,
        statusLabel: this.statusDisplayLabel(statusKey, statusRaw),
        volume,
        volumeLabel: volume == null || volume === ''
          ? '—'
          : Number(volume).toLocaleString('pt-BR'),
        valorLabel: '—',
        valorOrcamento: null,
        resultadoUrl: row.ResultadoUrl ?? row.resultadoUrl ?? null,
        dataLabel,
        sortDate: dataRaw ? new Date(dataRaw).getTime() : 0,
        filtrosSnapshot: this.extractFiltrosSnapshot(row),
        raw: row,
      }
    },

    /**
     * Recupera os filtros originais de uma linha da fila (quando a API os devolve),
     * para permitir Repetir/Duplicar.
     */
    extractFiltrosSnapshot(row) {
      if (!row || typeof row !== 'object') return null

      const embedded = row.Filtros ?? row.filtros ?? row.FiltrosJSON ?? row.filtrosJSON
      if (embedded) {
        if (typeof embedded === 'string') {
          try {
            const parsed = JSON.parse(embedded)
            if (parsed && typeof parsed === 'object') return parsed
          } catch {
            // segue para a extração campo a campo
          }
        } else if (typeof embedded === 'object') {
          return embedded
        }
      }

      const keys = [
        'genero', 'idadeMin', 'idade_min', 'idadeMax', 'idade_max',
        'rendaMin', 'renda_min', 'rendaMax', 'renda_max',
        'ufs', 'uf', 'codigosIBGE', 'codigos_ibge', 'bairro',
        'filtrarCelular', 'filtrar_celular', 'filtrarWhatsApp', 'filtrar_whatsapp',
        'filtrarEmail', 'filtrar_email', 'cbos', 'cbosExcluidos', 'cbos_excluidos',
      ]

      const snapshot = {}
      keys.forEach(key => {
        const pascal = key.charAt(0).toUpperCase() + key.slice(1)
        const value = row[key] != null ? row[key] : row[pascal]
        if (value != null && value !== '') snapshot[key] = value
      })

      return Object.keys(snapshot).length ? snapshot : null
    },

    canRepetirVolumetria(item) {
      return !!(item && item.tipo === 'b2c' && item.filtrosSnapshot)
    },

    normalizePersonalizadaItem(row) {
      if (!row || typeof row !== 'object') return null
      // Ignora linhas de totalização da SP
      if (row.TotalRegistros != null || row.totalRegistros != null) {
        if (row.Id == null && row.id == null && row.IdPersonalizada == null) return null
      }
      if (
        row.TotalAguardandoOrcamento != null
        || row.totalAguardandoOrcamento != null
      ) {
        return null
      }

      const id = row.IdPersonalizada
        ?? row.idPersonalizada
        ?? row.Id
        ?? row.id
      if (id == null || String(id).trim() === '') return null
      const n = Number(id)
      if (!Number.isFinite(n) || n <= 0) return null

      const statusRaw = row.Status ?? row.status ?? ''
      const statusKey = this.normalizeStatusKey(statusRaw)
      const valor = row.ValorOrcamento ?? row.valorOrcamento
      const dataRaw = row.DtSolicitacao
        ?? row.dtSolicitacao
        ?? row.CreatedAt
        ?? row.createdAt
        ?? row.DataInclusao
        ?? row.dataInclusao

      let dataLabel = '—'
      if (dataRaw) {
        const d = new Date(dataRaw)
        dataLabel = Number.isNaN(d.getTime())
          ? String(dataRaw)
          : d.toLocaleString('pt-BR')
      }

      const modoCobrancaRaw = String(
        row.ModoCobranca ?? row.modoCobranca ?? '',
      ).trim().toLowerCase()

      return {
        id: n,
        tipo: 'personalizada',
        nome: row.NomeConsulta ?? row.nomeConsulta ?? row.Nome ?? row.nome ?? '—',
        status: statusRaw,
        statusKey,
        statusLabel: this.statusDisplayLabel(statusKey, statusRaw),
        volume: null,
        volumeLabel: '—',
        valorOrcamento: valor != null ? Number(valor) : null,
        valorLabel: valor != null && Number.isFinite(Number(valor))
          ? Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : '—',
        modoCobranca: modoCobrancaRaw || null,
        resultadoUrl: row.ResultadoUrl ?? row.resultadoUrl ?? null,
        resultadoDescricao: row.ResultadoDescricao ?? row.resultadoDescricao ?? null,
        observacoesAdmin: row.ObservacoesAdmin
          ?? row.observacoesAdmin
          ?? row.Observacoes
          ?? row.observacoes
          ?? null,
        prazo: row.PrazoEntrega
          ?? row.prazoEntrega
          ?? row.Prazo
          ?? row.prazo
          ?? null,
        motivoRejeicao: row.MotivoRejeicao ?? row.motivoRejeicao ?? null,
        dataLabel,
        sortDate: dataRaw ? new Date(dataRaw).getTime() : 0,
        raw: row,
      }
    },

    normalizeStatusKey(raw) {
      const s = String(raw || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      if (s.includes('rejeit')) return 'rejeitado'
      if (s.includes('conclu') || s.includes('entreg')) return 'concluido'
      if (s.includes('exec') || s.includes('process')) return 'em_execucao'
      if (s.includes('aprov')) return 'aprovado'
      if (s.includes('orcamento') && (s.includes('env') || s.includes('enviad'))) {
        return 'orcamento_enviado'
      }
      if (s.includes('aguard') && s.includes('orcamento')) return 'aguardando_orcamento'
      if (s.includes('aguard') || s.includes('fila') || s.includes('pend')) return 'aguardando'
      if (s.includes('erro') || s.includes('cancel')) return 'erro'
      return s || 'aguardando'
    },

    statusDisplayLabel(statusKey, fallback) {
      const map = {
        aguardando: 'Na fila',
        aguardando_orcamento: 'Aguardando orçamento',
        orcamento_enviado: 'Aguardando aprovação',
        aprovado: 'Aprovado',
        rejeitado: 'Rejeitado',
        em_execucao: 'Em execução',
        concluido: 'Concluído',
        erro: 'Erro',
      }
      return map[statusKey] || fallback || '—'
    },

    filaStatusColor(status) {
      const s = this.normalizeStatusKey(status)
      if (s === 'concluido' || s === 'aprovado') return 'success'
      if (s === 'erro' || s === 'rejeitado') return 'error'
      if (s === 'em_execucao' || s === 'orcamento_enviado') return 'primary'
      if (s === 'aguardando' || s === 'aguardando_orcamento') return 'warning'
      return 'grey'
    },

    canExecutarB2c(item) {
      if (item?.tipo !== 'b2c') return false
      const s = item.statusKey
      return s === 'aguardando' || s === 'erro'
    },

    canEnriquecerLeads(item) {
      if (item?.tipo !== 'b2c') return false
      if (item.statusKey !== 'concluido') return false
      const volume = Number(item.volume)
      return Number.isFinite(volume) && volume > 0
    },

    canAprovarOrcamento(item) {
      return item?.tipo === 'personalizada' && item?.statusKey === 'orcamento_enviado'
    },

    openAprovarOrcamento(item) {
      if (!this.canAprovarOrcamento(item)) return
      this.orcamentoSelected = item
      this.orcamentoDialogMode = 'aprovar'
      this.orcamentoMotivo = ''
      this.orcamentoDialog = true
    },

    openRecusarOrcamento(item) {
      if (!this.canAprovarOrcamento(item)) return
      this.orcamentoSelected = item
      this.orcamentoDialogMode = 'recusar'
      this.orcamentoMotivo = ''
      this.orcamentoDialog = true
    },

    closeOrcamentoDialog() {
      if (this.orcamentoSaving) return
      this.orcamentoDialog = false
      this.orcamentoSelected = null
      this.orcamentoMotivo = ''
    },

    async confirmarAprovarOrcamento() {
      if (!this.orcamentoSelected?.id) return
      this.orcamentoSaving = true
      this.actionLoadingId = this.orcamentoSelected.id
      this.actionType = 'aprovar-orcamento'
      try {
        const payload = { novoStatus: 'aprovado' }
        if (this.orcamentoSelected.modoCobranca) {
          payload.modoCobranca = this.orcamentoSelected.modoCobranca
        }
        await atualizarPersonalizadaVolumetria(this.orcamentoSelected.id, payload)
        const debitou = this.orcamentoSelected.modoCobranca === 'descontar_saldo'
        this.$notify(
          debitou
            ? 'Orçamento aprovado. Valor debitado do seu saldo.'
            : 'Orçamento aprovado com sucesso.',
          'success',
        )
        this.orcamentoDialog = false
        this.orcamentoSelected = null
        await this.loadFilaLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível aprovar o orçamento.'
        this.$notify(message, 'error')
      } finally {
        this.orcamentoSaving = false
        this.actionLoadingId = null
        this.actionType = ''
      }
    },

    async confirmarRecusarOrcamento() {
      if (!this.orcamentoSelected?.id) return
      const motivo = String(this.orcamentoMotivo || '').trim()
      if (!motivo) return
      this.orcamentoSaving = true
      this.actionLoadingId = this.orcamentoSelected.id
      this.actionType = 'recusar-orcamento'
      try {
        await atualizarPersonalizadaVolumetria(this.orcamentoSelected.id, {
          novoStatus: 'rejeitado',
          motivoRejeicao: motivo,
        })
        this.$notify('Orçamento recusado.', 'success')
        this.orcamentoDialog = false
        this.orcamentoSelected = null
        this.orcamentoMotivo = ''
        await this.loadFilaLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível recusar o orçamento.'
        this.$notify(message, 'error')
      } finally {
        this.orcamentoSaving = false
        this.actionLoadingId = null
        this.actionType = ''
      }
    },

    irParaEnriquecerLeads(item) {
      if (!item?.id) return
      this.$router.push({
        name: 'serviceVolumetryEnrich',
        query: {
          idFila: String(item.id),
          nome: item.nome || 'Volumetria',
          volume: String(item.volume || 0),
        },
      })
    },

    async loadFilaLista(options = {}) {
      const silent = options && options.silent === true
      if (!silent) this.loadingFila = true
      try {
        const ultimosDias = Number(this.listaUltimosDias) || 60
        const [filaData, personalizadasData] = await Promise.all([
          getFilaVolumetria({
            pagina: 1,
            tamanhoPagina: 50,
            ultimosDias,
          }).catch(error => {
            console.warn('[volumetria] fila indisponível', error?.response?.data || error?.message)
            return { consultas: [] }
          }),
          listarPersonalizadasVolumetria({
            pagina: 1,
            tamanhoPagina: 50,
            ultimosDias,
          }).catch(error => {
            console.warn('[volumetria] personalizadas indisponível', error?.response?.data || error?.message)
            return []
          }),
        ])

        const filaRows = Array.isArray(filaData?.consultas)
          ? filaData.consultas
          : (Array.isArray(filaData) ? filaData : [])
        const personalizadasRows = Array.isArray(personalizadasData)
          ? personalizadasData
          : (Array.isArray(personalizadasData?.items) ? personalizadasData.items : [])

        const merged = [
          ...filaRows.map(this.normalizeFilaItem).filter(Boolean),
          ...personalizadasRows.map(this.normalizePersonalizadaItem).filter(Boolean),
        ].sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0))

        this.filaLista = merged
        this.syncFilaPolling()
      } catch (error) {
        if (!silent) {
          this.filaLista = []
          const message = error?.response?.data?.message
            || error?.message
            || 'Não foi possível carregar a lista de volumetrias.'
          this.$notify(message, 'error')
        } else {
          console.warn('[volumetria] polling da lista falhou', error?.message || error)
        }
      } finally {
        if (!silent) this.loadingFila = false
      }
    },

    hasPendingFilaItems() {
      return (this.filaLista || []).some(item => POLLING_STATUS_KEYS.includes(item.statusKey))
    },

    syncFilaPolling() {
      if (this.activeTab === 1 && this.hasPendingFilaItems()) {
        this.startFilaPolling()
      } else {
        this.stopFilaPolling()
      }
    },

    startFilaPolling() {
      if (this._filaPollTimer) return
      this._filaPollTimer = setInterval(() => {
        if (this.activeTab !== 1) {
          this.stopFilaPolling()
          return
        }
        this.loadFilaLista({ silent: true })
      }, POLL_INTERVAL_MS)
    },

    stopFilaPolling() {
      if (this._filaPollTimer) {
        clearInterval(this._filaPollTimer)
        this._filaPollTimer = null
      }
    },

    async executarItemB2c(item) {
      if (!item?.id) return
      this.actionLoadingId = item.id
      this.actionType = 'executar'
      try {
        await executarPedidoFilaVolumetria(item.id)
        this.$notify('Execução do pedido iniciada.', 'success')
        await this.loadFilaLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível executar o pedido.'
        this.$notify(message, 'error')
      } finally {
        this.actionLoadingId = null
        this.actionType = ''
      }
    },

    async loadCotaDiaria() {
      try {
        const data = await getCotaDiaria()
        const cota = normalizeCotaDiaria(data)
        this.cotaInfo = cota
        this.b2cQuotaUsed = cota.usadasHoje
        this.b2cQuotaLimit = cota.limiteDiario || this.b2cQuotaLimit
        return cota
      } catch (error) {
        // Mantém valores default se integração não estiver configurada
        console.warn('[volumetria] cota-diaria indisponível', error?.response?.data || error?.message)
        return this.cotaInfo
      }
    },

    formatCurrencyBRL(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },

    async loadBlacklists() {
      try {
        const data = await listarBlacklistsCbo()
        const list = (data?.blacklists || data || [])
          .map(normalizeBlacklistItem)
          .filter(Boolean)
        if (list.length) {
          this.savedBlacklists.splice(0, this.savedBlacklists.length, ...list)
        }
      } catch (error) {
        console.warn('[volumetria] blacklist indisponível', error?.response?.data || error?.message)
      }
    },

    async removeSavedBlacklist(item) {
      if (!item || item.id == null) return
      const confirmed = window.confirm(`Excluir a blacklist "${item.name}"?`)
      if (!confirmed) return

      try {
        await excluirBlacklistCbo(item.id)
        this.$notify('Blacklist excluída.', 'success')
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível excluir a blacklist.'
        this.$notify(message, 'error')
        return
      }

      // Remove os CBOs dessa blacklist dos filtros ativos
      if (this.isBlacklistActive(item.id)) this.toggleSavedBlacklist(item)
      this.savedBlacklists = this.savedBlacklists.filter(
        bl => String(bl.id) !== String(item.id),
      )
      await this.loadBlacklists()
    },

    async loadModelos() {
      this.loadingModelos = true
      try {
        const data = await listarModelosVolumetria()
        const rows = Array.isArray(data)
          ? data
          : (data?.modelos || data?.items || [])
        this.modelosLista = (Array.isArray(rows) ? rows : [])
          .map(normalizeModeloItem)
          .filter(Boolean)
      } catch (error) {
        console.warn('[volumetria] modelos indisponíveis', error?.response?.data || error?.message)
        this.modelosLista = []
      } finally {
        this.loadingModelos = false
      }
    },

    async excluirModelo(item) {
      if (!item || item.id == null) return
      const confirmed = window.confirm(`Excluir o modelo "${item.nome}"?`)
      if (!confirmed) return

      try {
        await excluirModeloVolumetria(item.id)
        this.modelosLista = this.modelosLista.filter(
          modelo => String(modelo.id) !== String(item.id),
        )
        this.$notify('Modelo excluído.', 'success')
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível excluir o modelo.'
        this.$notify(message, 'error')
      }
    },

    async carregarModelo(item) {
      if (!item) return
      this.queryName = String(item.nome || '').slice(0, 120)
      this.activeTab = 0
      await this.requestCotaGate('b2c', item.filtros)
    },

    /**
     * Aplica filtros vindos da API (formato enfileirar camelCase ou snake_case)
     * de volta nos filtros do wizard.
     */
    applyFiltrosFromApi(filtrosRaw) {
      let filtros = filtrosRaw
      if (typeof filtros === 'string') {
        try {
          filtros = JSON.parse(filtros)
        } catch {
          filtros = null
        }
      }
      if (!filtros || typeof filtros !== 'object') return

      const pick = (...keys) => {
        for (const key of keys) {
          if (filtros[key] != null && filtros[key] !== '') return filtros[key]
        }
        return undefined
      }

      const toNumberOrNull = value => {
        if (value == null || value === '') return null
        const n = Number(value)
        return Number.isFinite(n) ? n : null
      }

      const toList = value => {
        if (value == null || value === '') return []
        if (Array.isArray(value)) return value
        return String(value).split(',').map(v => v.trim()).filter(Boolean)
      }

      const genero = String(pick('genero', 'Genero', 'sexo') || '').toUpperCase()
      if (genero === 'M') this.filters.genero = 'Masculino'
      else if (genero === 'F') this.filters.genero = 'Feminino'
      else this.filters.genero = 'Ambos'

      const idadeMin = toNumberOrNull(pick('idadeMin', 'idade_min', 'IdadeMin'))
      const idadeMax = toNumberOrNull(pick('idadeMax', 'idade_max', 'IdadeMax'))
      this.filters.idadeMin = idadeMin
      this.filters.idadeMax = idadeMax

      const rendaMin = toNumberOrNull(pick('rendaMin', 'renda_min', 'RendaMin'))
      const rendaMax = toNumberOrNull(pick('rendaMax', 'renda_max', 'RendaMax'))
      this.filters.rendaMin = rendaMin
      this.filters.rendaMax = rendaMax
      if (rendaMin == null && rendaMax == null) {
        this.filters.rendaPreset = 'qualquer'
      } else {
        const preset = this.incomeOptions.find(option => (
          option.value !== 'personalizado'
          && option.value !== 'qualquer'
          && (option.min || null) === rendaMin
          && (option.max || null) === rendaMax
        ))
        this.filters.rendaPreset = preset ? preset.value : 'personalizado'
      }

      const ufs = toList(pick('ufs', 'UFs', 'uf')).map(uf => String(uf).toUpperCase())
      this.filters.ufs = ufs.filter(uf => BRAZILIAN_STATES.includes(uf))

      const codigos = toList(pick('codigosIBGE', 'codigos_ibge', 'CodigosIBGE'))
      this.filters.cidades = codigos.map(codigo => ({
        id: String(codigo),
        label: `IBGE ${codigo}`,
        name: `IBGE ${codigo}`,
        uf: this.filters.ufs[0] || '',
        codigoIBGE: Number(codigo) || codigo,
        ibge: Number(codigo) || codigo,
        placeholder: true,
      }))

      const bairros = toList(pick('bairro', 'bairros', 'Bairro'))
      this.filters.bairros = bairros.map(nome => ({
        id: `modelo:${nome}`,
        name: nome,
        nome,
        source: 'modelo',
      }))

      const canais = []
      if (Number(pick('filtrarCelular', 'filtrar_celular')) === 1) canais.push('celular')
      if (Number(pick('filtrarWhatsApp', 'filtrar_whatsapp')) === 1) canais.push('whatsapp')
      if (Number(pick('filtrarEmail', 'filtrar_email')) === 1) canais.push('email')
      this.filters.canais = canais

      const mapCbo = code => {
        const normalized = String(typeof code === 'object' ? code.code : code).trim()
        const found = this.findCboInCatalog(normalized)
        return found || { code: normalized, name: normalized }
      }

      this.filters.cbos = toList(pick('cbos', 'Cbos')).map(mapCbo).filter(c => c.code)
      this.filters.cbosExcluidos = toList(pick('cbosExcluidos', 'cbos_excluidos'))
        .map(mapCbo)
        .filter(c => c.code)
      this.filters.activeBlacklistIds = []

      if (this.filters.ufs.length) {
        this.ensureCitiesLoaded().then(() => this.resolveCidadesPlaceholders())
      }
    },

    /** Substitui cidades carregadas só pelo código IBGE pelos itens reais do cache. */
    resolveCidadesPlaceholders() {
      const pending = this.filters.cidades.filter(city => city.placeholder)
      if (!pending.length) return

      const all = []
      this.filters.ufs.forEach(uf => {
        const cached = this.citiesCache[uf]
        if (cached && cached.length) all.push(...cached)
      })
      if (!all.length) return

      this.filters.cidades = this.filters.cidades.map(city => {
        if (!city.placeholder) return city
        const match = all.find(
          option => String(option.codigoIBGE) === String(city.codigoIBGE),
        )
        return match || city
      })
    },

    async loadEstados() {
      try {
        const data = await getEstadosVolumetria()
        const estados = (data?.estados || data || [])
          .map(normalizeEstado)
          .filter(Boolean)
        const map = {}
        estados.forEach(estado => {
          map[estado.uf] = estado
        })
        this.estadosByUf = map
      } catch (error) {
        console.warn('[volumetria] estados indisponíveis', error?.response?.data || error?.message)
      }
    },

    async loadProfissoes(force = false) {
      if (this.cboCatalogLoading) return
      if (this.cboCatalogLoaded && !force) return

      this.cboCatalogLoading = true
      try {
        // Endpoint de CBOs da volumetria (GET /getCbos).
        let raw = null
        try {
          raw = await getCbosProspeccao()
        } catch (primaryError) {
          console.warn(
            '[volumetria] getCbos indisponível, tentando profissoes v2',
            primaryError?.response?.data || primaryError?.message,
          )
        }

        let rows = normalizeCboApiRows(raw)
        if (!rows.length) {
          const data = await getProfissoesVolumetria()
          rows = normalizeCboApiRows(data?.profissoes || data || [])
        }

        const catalog = normalizeProfissoesToCatalog(rows)
        if (catalog.length) {
          this.cboApiRows = rows
          this.cboCatalog = catalog
          this.cboCatalogLoaded = true
        }
      } catch (error) {
        console.warn(
          '[volumetria] CBOs indisponíveis (usando catálogo local)',
          error?.response?.data || error?.message,
        )
      } finally {
        this.cboCatalogLoading = false
      }
    },

    resetLocationState() {
      this.filters.ufs = []
      this.filters.cidades = []
      this.filters.bairros = []
      this.citySearch = ''
      this.bairroSearch = ''
      this.bairroSuggestions = []
      this.bairroDraft = null
      this.bairrosCache = {}
      this.zonePanelOpen = false
      this.expandedRegions = ['sao_paulo_sp']
      this.locationErrors = { ufs: [] }
    },

    async validateQueryName() {
      if (this.$refs.observer) {
        const isValid = await this.$refs.observer.validate()
        if (!isValid) return false

        return true
      }

      if (!String(this.queryName || '').trim()) {
        this.$notify('Informe o nome da consulta.', 'warning')
        return false
      }

      return true
    },

    resetWizardState() {
      Object.assign(this.filters, createDefaultFilters())
      this.resetLocationState()
      this.ageErrors = { min: [], max: [] }
      this.channelErrors = []
      this.incomeErrors = []
      this.resetCboState()
    },

    /**
     * Abre o modal de cota (gate) e guarda a ação pendente.
     * `filtros` permite pré-carregar filtros (modelo salvo, repetir/duplicar)
     * que serão aplicados somente após a confirmação.
     */
    async requestCotaGate(action, filtros = null) {
      this.pendingStartAction = action
      this.pendingFiltros = filtros
      this.cotaConfirming = true
      this.cotaDialog = true
      try {
        await this.loadCotaDiaria()
      } finally {
        this.cotaConfirming = false
      }
    },

    async startConsultation() {
      const isValid = await this.validateQueryName()
      if (!isValid) return

      await this.requestCotaGate('b2c')
    },

    closeCotaDialog() {
      if (this.cotaConfirming) return
      this.cotaDialog = false
      this.pendingStartAction = null
      this.pendingFiltros = null
    },

    confirmCotaAndStart() {
      const cota = this.cotaInfo
      if (cota && cota.podeProsseguir === false) return

      const action = this.pendingStartAction || 'b2c'
      const filtros = this.pendingFiltros
      this.cotaDialog = false
      this.pendingStartAction = null
      this.pendingFiltros = null

      this.successScreen = null
      this.flowMode = action === 'b2c' ? 'b2c' : action
      this.resetWizardState()
      if (filtros) this.applyFiltrosFromApi(filtros)
      this.descricaoLivre = ''
      this.modoCobranca = 'a_faturar'
      this.currentStep = 1
      this.wizardActive = true
    },

    /**
     * Fluxo personalizado: sem gate de cota.
     * direto = true pula o wizard e vai direto para a descrição livre (etapa 8).
     */
    async startPersonalizada(direto = false) {
      const isValid = await this.validateQueryName()
      if (!isValid) return

      this.successScreen = null
      this.flowMode = direto ? 'direto' : 'personalizada'
      this.resetWizardState()
      this.descricaoLivre = ''
      this.modoCobranca = 'a_faturar'
      this.currentStep = direto ? PERSONALIZADA_STEP : 1
      this.wizardActive = true
    },

    goToPersonalizadaStep() {
      this.currentStep = PERSONALIZADA_STEP
    },

    async submitPedidoPersonalizado() {
      if (this.submittingPersonalizada) return

      let body
      try {
        body = buildPersonalizadaVolumetriaBody({
          nomeConsulta: this.queryName,
          descricaoLivre: this.descricaoLivre,
          modoCobranca: this.modoCobranca,
          filters: this.flowMode === 'direto' ? null : this.filters,
        })
      } catch (error) {
        this.$notify(error.message || 'Dados inválidos para o pedido.', 'warning')
        return
      }

      this.submittingPersonalizada = true
      try {
        const { data } = await criarPersonalizadaVolumetria(body)
        const idPedido = data?.idPersonalizada
          ?? data?.IdPersonalizada
          ?? data?.idGerado
          ?? data?.id
          ?? null

        this.wizardActive = false
        this.successScreen = {
          tipo: 'pedido',
          id: idPedido,
          nome: String(this.queryName || '').trim(),
        }
        this.descricaoLivre = ''
        this.modoCobranca = 'a_faturar'
        this.flowMode = 'b2c'
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.response?.data?.error
          || error?.message
          || 'Não foi possível enviar o pedido personalizado.'
        this.$notify(message, 'error')
      } finally {
        this.submittingPersonalizada = false
      }
    },

    clearSuccessScreen(tab = 0) {
      this.successScreen = null
      this.queryName = ''
      this.flowMode = 'b2c'
      this.currentStep = 1

      // O watcher de activeTab já recarrega a lista quando a aba muda
      const tabChanged = this.activeTab !== tab
      this.activeTab = tab
      if (tab === 1 && !tabChanged) this.loadFilaLista()
    },

    maybeOpenTourOnFirstVisit() {
      let concluido = null
      try {
        concluido = window.localStorage.getItem(TOUR_STORAGE_KEY)
      } catch {
        concluido = '1'
      }
      if (concluido) return

      this._tourTimer = setTimeout(() => {
        this.tourStep = 1
        this.tourDialog = true
      }, 800)
    },

    openTour() {
      this.tourStep = 1
      this.tourDialog = true
    },

    closeTour() {
      this.tourDialog = false
      try {
        window.localStorage.setItem(TOUR_STORAGE_KEY, '1')
      } catch {
        // localStorage indisponível (modo privado) — segue sem persistir
      }
    },

    async repetirVolumetria(item, modo = 'repetir') {
      if (!item) return
      const snapshot = item.filtrosSnapshot
      if (!snapshot) {
        this.$notify('Não foi possível recuperar os filtros desta volumetria.', 'warning')
        return
      }

      const sufixo = modo === 'duplicar' ? ' (cópia)' : ' (repetição)'
      this.queryName = `${item.nome || 'Volumetria'}${sufixo}`.slice(0, 120)
      this.activeTab = 0
      await this.requestCotaGate('b2c', snapshot)
    },

    selectGender(value) {
      this.filters.genero = value
      this.currentStep = 2
    },

    normalizeAge(value) {
      if (value === '' || value === null || value === undefined || Number.isNaN(value)) {
        return null
      }

      return Number(value)
    },

    applyAgeShortcut(shortcut) {
      this.filters.idadeMin = shortcut.min
      this.filters.idadeMax = shortcut.max
      this.ageErrors = { min: [], max: [] }
    },

    isAgeShortcutActive(shortcut) {
      return this.normalizeAge(this.filters.idadeMin) === shortcut.min
        && this.normalizeAge(this.filters.idadeMax) === shortcut.max
    },

    validateAgeRange() {
      const min = this.normalizeAge(this.filters.idadeMin)
      const max = this.normalizeAge(this.filters.idadeMax)
      const errors = { min: [], max: [] }

      if (min !== null && (min < 0 || min > 120)) {
        errors.min.push('Informe uma idade entre 0 e 120')
      }

      if (max !== null && (max < 0 || max > 120)) {
        errors.max.push('Informe uma idade entre 0 e 120')
      }

      if (min !== null && max !== null && min > max) {
        errors.min.push('A idade mínima não pode ser maior que a máxima')
      }

      this.ageErrors = errors
      this.filters.idadeMin = min
      this.filters.idadeMax = max

      return errors.min.length === 0 && errors.max.length === 0
    },

    confirmAgeRange() {
      if (!this.validateAgeRange()) return
      this.currentStep = 3
    },

    selectIncome(option) {
      this.filters.rendaPreset = option.value

      if (option.value === 'personalizado') {
        this.filters.rendaMin = null
        this.filters.rendaMax = null
        this.incomeErrors = []

        return
      }

      this.filters.rendaMin = option.min
      this.filters.rendaMax = option.max
      this.incomeErrors = []
      this.currentStep = 4
    },

    confirmCustomIncome() {
      const min = this.filters.rendaMin === '' || this.filters.rendaMin == null
        ? null
        : Number(this.filters.rendaMin)
      const max = this.filters.rendaMax === '' || this.filters.rendaMax == null
        ? null
        : Number(this.filters.rendaMax)

      if (min !== null && (!Number.isFinite(min) || min < 0)) {
        this.incomeErrors = ['Informe uma renda mínima válida']
        return
      }

      if (max !== null && (!Number.isFinite(max) || max < 0)) {
        this.incomeErrors = ['Informe uma renda máxima válida']
        return
      }

      if (min === null && max === null) {
        this.incomeErrors = ['Informe a renda mínima e/ou máxima']
        return
      }

      if (min !== null && max !== null && min > max) {
        this.incomeErrors = ['A renda mínima não pode ser maior que a máxima']
        return
      }

      this.filters.rendaMin = min
      this.filters.rendaMax = max
      this.incomeErrors = []
      this.currentStep = 4
    },

    isStateSelected(uf) {
      return this.filters.ufs.includes(uf)
    },

    toggleState(uf) {
      if (this.neighborhoodModeActive) return

      const index = this.filters.ufs.indexOf(uf)

      if (index >= 0) {
        this.filters.ufs.splice(index, 1)
        this.filters.cidades = this.filters.cidades.filter(city => city.uf !== uf)
      } else {
        this.filters.ufs.push(uf)
      }

      if (this.filters.ufs.length) {
        this.locationErrors = { ufs: [] }
      }

      this.ensureCitiesLoaded()
    },

    selectAllStates() {
      if (this.neighborhoodModeActive) return
      this.filters.ufs = [...BRAZILIAN_STATES]
      this.locationErrors = { ufs: [] }
      this.ensureCitiesLoaded()
    },

    clearStates() {
      if (this.neighborhoodModeActive) return
      this.resetLocationState()
    },

    toggleZonePanel() {
      if (!this.filters.ufs.length) return
      this.zonePanelOpen = !this.zonePanelOpen
    },

    toggleRegion(regionId) {
      const index = this.expandedRegions.indexOf(regionId)
      if (index >= 0) {
        this.expandedRegions.splice(index, 1)
      } else {
        this.expandedRegions.push(regionId)
      }
    },

    isRegionExpanded(regionId) {
      return this.expandedRegions.includes(regionId)
    },

    getZoneSelectedCount(zone) {
      return zone.neighborhoods.filter(name => (
        this.filters.bairros.some(item => item.zoneId === zone.id && item.name === name)
      )).length
    },

    getZonePreview(zone) {
      const preview = zone.neighborhoods.slice(0, 3).join(', ')
      const remaining = zone.neighborhoods.length - 3
      return remaining > 0 ? `${preview}... +${remaining} mais...` : preview
    },

    addZoneNeighborhoods(zone, region) {
      zone.neighborhoods.forEach(name => {
        const exists = this.filters.bairros.some(
          item => item.zoneId === zone.id && item.name === name,
        )
        if (!exists) {
          this.filters.bairros.push({
            id: `${zone.id}:${name}`,
            name,
            nome: name,
            zoneId: zone.id,
            regionId: region.id,
            uf: region.uf,
            source: 'zone',
          })
        }
      })

      this.filters.cidades = []
      this.citySearch = ''
      this.bairroSearch = ''
      this.bairroSuggestions = []
      this.bairroDraft = null
    },

    removeZoneNeighborhoods(zone) {
      this.filters.bairros = this.filters.bairros.filter(item => item.zoneId !== zone.id)
    },

    clearAllNeighborhoods() {
      this.filters.bairros = []
      this.bairroSearch = ''
      this.bairroSuggestions = []
      this.bairroDraft = null
    },

    removeBairro(id) {
      this.filters.bairros = this.filters.bairros.filter(item => item.id !== id)
    },

    filterCitiesAccentInsensitive(item, queryText, itemText) {
      const text = normalizarTextoBusca(itemText || item.label || item.name || '')
      const query = normalizarTextoBusca(queryText || '')
      return text.indexOf(query) > -1
    },

    normalizeCityItem(raw, uf) {
      const name = raw.nome || raw.name || raw.cidade || raw.Cidade || raw.Nome || String(raw)
      const id = raw.id || raw.codigoIBGE || raw.CodigoIBGE || `${uf}-${name}`
      return {
        id: String(id),
        label: `${name} - ${uf}`,
        name,
        uf,
        codigoIBGE: raw.codigoIBGE || raw.CodigoIBGE || raw.id || null,
        ibge: raw.codigoIBGE || raw.CodigoIBGE || raw.id || null,
      }
    },

    async fetchCitiesForUf(uf) {
      if (this.citiesCache[uf]) return

      this.citiesLoading = true
      try {
        const res = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`,
        )
        if (!res.ok) throw new Error(`IBGE status ${res.status}`)
        const data = await res.json()
        const cidades = (Array.isArray(data) ? data : []).map(m => mapIbgeMunicipio(m, uf))
        this.$set(this.citiesCache, uf, cidades)
      } catch (error) {
        console.warn(`[volumetria] IBGE cidades ${uf} indisponíveis`, error?.message || error)
        this.$set(this.citiesCache, uf, [])
      } finally {
        this.citiesLoading = false
      }
    },

    async ensureCitiesLoaded() {
      if (!this.filters.ufs.length) return
      await Promise.all(this.filters.ufs.map(uf => this.fetchCitiesForUf(uf)))
    },

    onBairroSearchInput(value) {
      if (this._bairroSearchTimer) clearTimeout(this._bairroSearchTimer)
      this._bairroSearchTimer = setTimeout(() => {
        this.buscarBairrosApi(value)
      }, 350)
    },

    cidadeBairroCacheKey(cidade) {
      if (!cidade) return ''
      const ibge = cidade.codigoIBGE || cidade.codigoIbge || cidade.ibge || cidade.id
      const nome = cidade.name || cidade.nome || ''
      const uf = cidade.uf || ''
      return String(ibge || `${nome}|${uf}`)
    },

    normalizeBairroApiItem(raw, cidade) {
      const cidadeNome = cidade.name || cidade.nome || ''
      const uf = cidade.uf || ''
      let nome = ''
      let codigo = null

      if (typeof raw === 'string') {
        nome = raw.trim()
      } else if (raw && typeof raw === 'object') {
        nome = String(
          raw.Nome || raw.nome || raw.name || raw.Bairro || raw.bairro || '',
        ).trim()
        codigo = raw.Codigo ?? raw.codigo ?? raw.id ?? null
      } else {
        nome = String(raw || '').trim()
      }

      if (!nome) return null

      return {
        id: codigo != null
          ? `df:${codigo}`
          : `df:${cidade.codigoIBGE || cidade.ibge || cidade.id}:${nome}`,
        name: nome,
        nome,
        codigo,
        cidade: cidadeNome,
        uf,
        source: 'api',
      }
    },

    async ensureBairrosLoaded(cidade) {
      const key = this.cidadeBairroCacheKey(cidade)
      if (!key || this.bairrosCache[key]) return this.bairrosCache[key] || []

      this.bairrosLoading = true
      try {
        const cidadeNome = cidade.name || cidade.nome || ''
        const uf = String(cidade.uf || '').toUpperCase()
        const ibgeCode = String(
          cidade.codigoIBGE || cidade.codigoIbge || cidade.ibge || cidade.id || '',
        ).replace(/\D/g, '')

        let rawList = []

        // Preferência: lista completa de bairros da cidade.
        // Fallback: LLM por código IBGE (mesmo endpoint do prospeccao-ia).
        if (cidadeNome && uf) {
          try {
            const fallback = await getBairrosPorCidadeProspeccao(cidadeNome, uf)
            rawList = Array.isArray(fallback?.bairros) ? fallback.bairros : []
          } catch (apiError) {
            console.warn(
              '[volumetria] getBairrosByCidade indisponível, tentando LLM',
              apiError?.message || apiError,
            )
          }
        }

        if (!rawList.length && ibgeCode.length === 7) {
          try {
            const response = await getBairrosPorCodigoIbgeProspeccao({
              ibgeCode,
              cityName: cidadeNome,
              uf,
            })
            if (Array.isArray(response?.bairros)) {
              rawList = response.bairros
            } else if (Array.isArray(response?.neighborhoods)) {
              rawList = response.neighborhoods
            } else if (Array.isArray(response)) {
              rawList = response
            }
          } catch (llmError) {
            console.warn(
              '[volumetria] LLM bairros indisponível',
              llmError?.message || llmError,
            )
          }
        }

        const mapped = rawList
          .map(item => this.normalizeBairroApiItem(item, cidade))
          .filter(Boolean)

        const unicos = []
        const seen = new Set()
        mapped.forEach(item => {
          const k = normalizarTextoBusca(item.name)
          if (!k || seen.has(k)) return
          seen.add(k)
          unicos.push(item)
        })

        this.$set(this.bairrosCache, key, unicos)
        return unicos
      } catch (error) {
        console.warn('[volumetria] bairros indisponíveis', error?.message || error)
        this.$set(this.bairrosCache, key, [])
        return []
      } finally {
        this.bairrosLoading = false
      }
    },

    async buscarBairrosApi(q) {
      if (!q || String(q).trim().length < 2 || this.filters.cidades.length !== 1) {
        this.bairroSuggestions = []
        return
      }

      const cidade = this.filters.cidades[0]
      const lista = await this.ensureBairrosLoaded(cidade)
      const qNorm = normalizarTextoBusca(q.trim())

      this.bairroSuggestions = (lista || [])
        .filter(b => normalizarTextoBusca(b.name).includes(qNorm))
        .slice(0, 15)
    },

    onBairroDraftSelected(item) {
      if (!item || typeof item !== 'object') return
      const exists = this.filters.bairros.some(
        b => b.name === item.name || b.id === item.id,
      )
      if (!exists) {
        this.filters.bairros.push({
          id: item.id,
          name: item.name,
          nome: item.name,
          cidade: item.cidade,
          uf: item.uf,
          source: item.source || 'api',
        })
      }
      this.$nextTick(() => {
        this.bairroDraft = null
        this.bairroSearch = ''
        this.bairroSuggestions = []
      })
    },

    validateLocation() {
      const errors = { ufs: [] }

      if (!this.filters.ufs.length) {
        errors.ufs.push('Selecione pelo menos um estado')
      }

      this.locationErrors = errors
      return errors.ufs.length === 0
    },

    confirmLocation() {
      if (!this.validateLocation()) return
      this.currentStep = 5
    },

    isChannelSelected(value) {
      return this.filters.canais.includes(value)
    },

    toggleChannel(value) {
      const index = this.filters.canais.indexOf(value)

      if (index >= 0) {
        this.filters.canais.splice(index, 1)
      } else {
        this.filters.canais.push(value)
      }

      if (this.filters.canais.length) {
        this.channelErrors = []
      }
    },

    confirmChannels() {
      if (!this.filters.canais.length) {
        this.channelErrors = ['Selecione pelo menos um canal de contato']
        return
      }

      this.channelErrors = []
      this.currentStep = 6
    },

    resetCboState() {
      this.filters.cbos = []
      this.filters.cbosExcluidos = []
      this.filters.activeBlacklistIds = []
      this.cboPasteText = ''
      this.cboPasteError = ''
      this.blacklistPanelOpen = false
      this.cancelBlacklistCreate()
      this.closeCboSearchModal()
      this.loadBlacklists()
    },

    openCboSearchModal(target = 'include') {
      this.cboSearchTarget = target
      this.cboSearchQuery = ''
      this.cboModalSelection = []
      this.cboExpandedFamilies = []
      this.showCboSearchModal = true
      // Garante lista completa ao abrir o modal de CBO.
      this.loadProfissoes()
    },

    closeCboSearchModal() {
      this.showCboSearchModal = false
      this.cboSearchQuery = ''
      this.cboModalSelection = []
      this.cboExpandedFamilies = []
      this.cboSearchTarget = 'include'
    },

    applyCboSuggestion(suggestion) {
      this.cboSearchQuery = suggestion
      this.onCboSearchInput()
    },

    onCboSearchInput() {
      this.$nextTick(() => {
        this.cboExpandedFamilies = this.cboSearchResults.map(family => family.familyCode)
      })
    },

    findCboInCatalog(code) {
      const normalized = String(code).trim()
      for (const family of this.cboCatalog) {
        const occupation = family.occupations.find(item => item.code === normalized)
        if (occupation) {
          return {
            code: occupation.code,
            name: occupation.name,
            familyCode: family.familyCode,
          }
        }
      }
      return null
    },

    isCboModalSelected(code) {
      return this.cboModalSelection.some(item => item.code === code)
    },

    isCboFamilyExpanded(familyCode) {
      return this.cboExpandedFamilies.includes(familyCode)
    },

    toggleCboFamilyExpanded(familyCode) {
      const index = this.cboExpandedFamilies.indexOf(familyCode)
      if (index >= 0) {
        this.cboExpandedFamilies.splice(index, 1)
      } else {
        this.cboExpandedFamilies.push(familyCode)
      }
    },

    isCboFamilyFullySelected(family) {
      return family.occupations.every(item => this.isCboModalSelected(item.code))
    },

    toggleCboModalOccupation(occupation) {
      const index = this.cboModalSelection.findIndex(item => item.code === occupation.code)
      if (index >= 0) {
        this.cboModalSelection.splice(index, 1)
        return
      }

      this.cboModalSelection.push({
        code: occupation.code,
        name: occupation.name,
      })
    },

    toggleCboFamilySelection(family) {
      if (this.isCboFamilyFullySelected(family)) {
        const codes = new Set(family.occupations.map(item => item.code))
        this.cboModalSelection = this.cboModalSelection.filter(item => !codes.has(item.code))
        return
      }

      family.occupations.forEach(occupation => {
        if (!this.isCboModalSelected(occupation.code)) {
          this.cboModalSelection.push({
            code: occupation.code,
            name: occupation.name,
          })
        }
      })

      if (!this.isCboFamilyExpanded(family.familyCode)) {
        this.cboExpandedFamilies.push(family.familyCode)
      }
    },

    mergeCbosIntoList(targetList, items) {
      items.forEach(item => {
        if (!targetList.some(cbo => cbo.code === item.code)) {
          targetList.push({ ...item })
        }
      })
    },

    addSelectedCbosFromModal() {
      if (this.cboSearchTarget === 'blacklist') {
        this.mergeCbosIntoList(this.blacklistDraft.cbos, this.cboModalSelection)
      } else {
        this.mergeCbosIntoList(this.filters.cbos, this.cboModalSelection)
      }
      this.closeCboSearchModal()
    },

    validateAndAddCboCodes() {
      const raw = this.cboPasteText.trim()
      if (!raw) return

      const codes = raw
        .split(/[\s,;/\n]+/)
        .map(code => code.trim())
        .filter(Boolean)

      const found = []
      const invalid = []

      codes.forEach(code => {
        const match = this.findCboInCatalog(code)
        if (match) found.push(match)
        else invalid.push(code)
      })

      if (!found.length) {
        this.cboPasteError = invalid.length
          ? `Código(s) não encontrado(s): ${invalid.join(', ')}`
          : 'Informe ao menos um código CBO válido'
        return
      }

      this.mergeCbosIntoList(this.filters.cbos, found)

      this.cboPasteText = ''
      this.cboPasteError = invalid.length
        ? `Alguns códigos não foram encontrados: ${invalid.join(', ')}`
        : ''
    },

    removeCbo(code) {
      this.filters.cbos = this.filters.cbos.filter(item => item.code !== code)
    },

    confirmCbos() {
      this.currentStep = 7
      this.blacklistPanelOpen = false
    },

    truncateLabel(value, max = 22) {
      if (!value) return ''
      return value.length > max ? `${value.slice(0, max)}...` : value
    },

    startBlacklistCreate() {
      this.showBlacklistCreate = true
      this.blacklistDraft.name = ''
      this.blacklistDraft.cbos = []
      this.blacklistDraftErrors = { name: [] }
      this.blacklistPasteText = ''
      this.blacklistPasteError = ''
      this.blacklistPanelOpen = true
    },

    cancelBlacklistCreate() {
      this.showBlacklistCreate = false
      this.blacklistDraft.name = ''
      this.blacklistDraft.cbos = []
      this.blacklistDraftErrors = { name: [] }
      this.blacklistPasteText = ''
      this.blacklistPasteError = ''
    },

    validateBlacklistPaste() {
      const raw = this.blacklistPasteText.trim()
      if (!raw) return

      const codes = raw
        .split(/[\s,;/\n]+/)
        .map(code => code.trim())
        .filter(Boolean)

      const found = []
      const invalid = []

      codes.forEach(code => {
        const match = this.findCboInCatalog(code)
        if (match) found.push(match)
        else invalid.push(code)
      })

      if (!found.length) {
        this.blacklistPasteError = invalid.length
          ? `Código(s) não encontrado(s): ${invalid.join(', ')}`
          : 'Informe ao menos um código CBO válido'
        return
      }

      this.mergeCbosIntoList(this.blacklistDraft.cbos, found)
      this.blacklistPasteText = ''
      this.blacklistPasteError = invalid.length
        ? `Alguns códigos não foram encontrados: ${invalid.join(', ')}`
        : ''
    },

    removeDraftBlacklistCbo(code) {
      this.blacklistDraft.cbos = this.blacklistDraft.cbos.filter(item => item.code !== code)
    },

    async saveAndApplyBlacklist() {
      const name = (this.blacklistDraft.name || '').trim()
      if (!name) {
        this.blacklistDraftErrors = { name: ['Informe o nome da blacklist'] }
        return
      }

      if (!this.blacklistDraft.cbos.length) {
        this.$notify('Adicione ao menos um CBO à blacklist.', 'warning')
        return
      }

      const payload = {
        nome: name,
        tipo: 'cbo',
        autoApply: 0,
        cbos: this.blacklistDraft.cbos.map(item => ({
          code: item.code,
          name: item.name,
        })),
      }

      try {
        const { data } = await salvarBlacklistCbo(payload)
        const saved = normalizeBlacklistItem(data) || {
          id: data?.id ?? data?.Id ?? `bl-${Date.now()}`,
          name,
          cbos: payload.cbos,
        }

        const existingIndex = this.savedBlacklists.findIndex(item => String(item.id) === String(saved.id))
        if (existingIndex >= 0) {
          this.$set(this.savedBlacklists, existingIndex, saved)
        } else {
          this.savedBlacklists.unshift(saved)
        }

        this.applyBlacklist(saved)
        this.cancelBlacklistCreate()
        this.$notify('Blacklist salva com sucesso.', 'success')
      } catch (error) {
        const message = error?.response?.data?.message
          || 'Não foi possível salvar a blacklist. Tentando salvar localmente.'
        this.$notify(message, 'warning')

        const localItem = {
          id: `bl-${Date.now()}`,
          name,
          cbos: this.blacklistDraft.cbos.map(item => ({ ...item })),
        }
        this.savedBlacklists.unshift(localItem)
        this.applyBlacklist(localItem)
        this.cancelBlacklistCreate()
      }
    },

    isBlacklistActive(id) {
      return this.filters.activeBlacklistIds.includes(id)
    },

    applyBlacklist(item) {
      this.mergeCbosIntoList(this.filters.cbosExcluidos, item.cbos)
      if (!this.filters.activeBlacklistIds.includes(item.id)) {
        this.filters.activeBlacklistIds.push(item.id)
      }
    },

    toggleSavedBlacklist(item) {
      if (this.isBlacklistActive(item.id)) {
        const codes = new Set(item.cbos.map(cbo => cbo.code))
        this.filters.cbosExcluidos = this.filters.cbosExcluidos.filter(cbo => !codes.has(cbo.code))
        this.filters.activeBlacklistIds = this.filters.activeBlacklistIds.filter(id => id !== item.id)

        // Reaplica outras blacklists ativas (evita remover CBOs compartilhados)
        this.filters.activeBlacklistIds.forEach(id => {
          const active = this.savedBlacklists.find(bl => bl.id === id)
          if (active) this.mergeCbosIntoList(this.filters.cbosExcluidos, active.cbos)
        })
        return
      }

      this.applyBlacklist(item)
    },

    removeExcludedCbo(code) {
      this.filters.cbosExcluidos = this.filters.cbosExcluidos.filter(item => item.code !== code)
      this.filters.activeBlacklistIds = this.filters.activeBlacklistIds.filter(id => {
        const item = this.savedBlacklists.find(bl => bl.id === id)
        if (!item) return false
        return item.cbos.every(cbo => this.filters.cbosExcluidos.some(ex => ex.code === cbo.code))
      })
    },

    clearExcludedCbos() {
      this.filters.cbosExcluidos = []
      this.filters.activeBlacklistIds = []
    },

    async saveFiltersAsTemplate() {
      try {
        const filtros = buildEnfileirarVolumetriaBody({
          nomeConsulta: this.queryName || 'Modelo sem nome',
          filters: this.filters,
        })

        await salvarModeloVolumetria({
          nome: this.queryName || `Modelo ${new Date().toLocaleString('pt-BR')}`,
          filtros: filtros,
        })

        this.$notify('Modelo de filtros salvo com sucesso.', 'success')
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível salvar o modelo de filtros.'
        this.$notify(message, 'error')
      }
    },

    async consultVolume() {
      if (this.consultingVolume) return

      if (!this.filters.canais.length) {
        this.$notify('Selecione pelo menos um canal de contato.', 'warning')
        return
      }

      let body
      try {
        body = buildEnfileirarVolumetriaBody({
          nomeConsulta: this.queryName,
          filters: this.filters,
        })
      } catch (error) {
        this.$notify(error.message || 'Filtros inválidos para consulta.', 'warning')
        return
      }

      this.consultingVolume = true
      try {
        const { data } = await enfileirarVolumetria(body)
        const idFila = data?.idFilaGerado ?? data?.idGerado ?? data?.IdFilaGerado
        await this.loadCotaDiaria()
        this.wizardActive = false
        this.successScreen = {
          tipo: 'fila',
          id: idFila || null,
          nome: String(this.queryName || '').trim(),
        }
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.response?.data?.error
          || 'Não foi possível consultar o volume. Tente novamente em instantes.'
        this.$notify(message, 'error')
      } finally {
        this.consultingVolume = false
      }
    },

    /** Último botão da etapa 7: consulta (b2c) ou segue para a descrição (personalizada). */
    finishFilterWizard() {
      if (this.flowMode === 'personalizada') {
        this.goToPersonalizadaStep()
        return
      }

      this.consultVolume()
    },

    goBack() {
      if (this.currentStep === PERSONALIZADA_STEP) {
        if (this.flowMode === 'personalizada') {
          this.currentStep = TOTAL_STEPS
          return
        }

        this.cancelAndRestart()
        return
      }

      if (this.currentStep > 1) {
        this.currentStep -= 1
        return
      }

      this.wizardActive = false
    },

    cancelAndRestart() {
      this.wizardActive = false
      this.successScreen = null
      this.flowMode = 'b2c'
      this.currentStep = 1
      Object.assign(this.filters, createDefaultFilters())
      this.resetLocationState()
      this.resetCboState()
      this.ageErrors = { min: [], max: [] }
      this.channelErrors = []
      this.incomeErrors = []
      this.descricaoLivre = ''
      this.modoCobranca = 'a_faturar'
      this.queryName = ''
      this.activeTab = 0
    },
  },
}
</script>

<style lang="scss" scoped>
.service-volumetry {
  ::v-deep .v-card.v-card--outlined {
    border-color: rgba(0, 0, 0, 0.12);
  }
}

.theme--dark .service-volumetry {
  ::v-deep .v-card.v-card--outlined {
    border-color: rgba(255, 255, 255, 0.18) !important;
  }
}

/* Chips de filtro / tipo / status — sem depender do primary quebrado no dark */
.vol-chip-filter {
  cursor: pointer;
  border-color: rgba(39, 21, 64, 0.35) !important;
  color: #271540 !important;
  background-color: transparent !important;

  &--active {
    border-color: #271540 !important;
    background-color: rgba(39, 21, 64, 0.12) !important;
    color: #271540 !important;
    font-weight: 600;
  }
}

.vol-chip-tipo {
  background-color: transparent !important;

  &--b2c {
    border-color: #271540 !important;
    color: #271540 !important;
  }

  &--orcamento {
    border-color: #5e5669 !important;
    color: #5e5669 !important;
  }
}

.vol-chip-status {
  color: #ffffff !important;

  &--success {
    background-color: #4caf50 !important;
    border-color: #4caf50 !important;
  }

  &--error {
    background-color: #b71c1c !important;
    border-color: #b71c1c !important;
  }

  &--warning {
    background-color: #ffb400 !important;
    border-color: #ffb400 !important;
    color: #1a1a1a !important;
  }

  &--primary,
  &--info {
    background-color: #327db8 !important;
    border-color: #327db8 !important;
  }

  &--grey {
    background-color: #78909c !important;
    border-color: #78909c !important;
  }
}

.theme--dark .service-volumetry {
  ::v-deep .vol-chip-filter {
    border-color: rgba(255, 255, 255, 0.35) !important;
    color: #e8eaed !important;
    background-color: transparent !important;
  }

  ::v-deep .vol-chip-filter--active {
    border-color: #c5b3ff !important;
    background-color: rgba(197, 179, 255, 0.18) !important;
    color: #e8eaed !important;
  }

  ::v-deep .vol-chip-tipo--b2c {
    border-color: #c5b3ff !important;
    color: #c5b3ff !important;
  }

  ::v-deep .vol-chip-tipo--orcamento {
    border-color: #90a4ae !important;
    color: #cfd8dc !important;
  }

  ::v-deep .vol-chip-status--warning {
    color: #1a1a1a !important;
  }

  /* Chips primary (preenchidos/outlined) — evita badge branco ilegível no dark */
  ::v-deep .v-chip.primary:not(.v-chip--outlined) {
    background-color: #5e35b1 !important;
    border-color: #5e35b1 !important;
    color: #ffffff !important;
  }

  ::v-deep .v-chip.primary:not(.v-chip--outlined) .v-chip__content,
  ::v-deep .v-chip.primary:not(.v-chip--outlined) .v-icon {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }

  ::v-deep .v-chip.v-chip--outlined.primary,
  ::v-deep .v-chip.v-chip--outlined.primary--text {
    background-color: transparent !important;
    border-color: #c5b3ff !important;
    color: #c5b3ff !important;
  }

  ::v-deep .v-chip.v-chip--outlined.primary .v-chip__content,
  ::v-deep .v-chip.v-chip--outlined.primary--text .v-chip__content,
  ::v-deep .v-chip.v-chip--outlined.primary .v-icon,
  ::v-deep .v-chip.v-chip--outlined.primary--text .v-icon {
    color: #c5b3ff !important;
    caret-color: #c5b3ff !important;
  }

  /* Botões primary preenchidos — evita "Continuar/Próximo" branco no dark */
  ::v-deep .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) {
    background-color: #5e35b1 !important;
    border-color: #5e35b1 !important;
    color: #ffffff !important;
  }

  ::v-deep .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) .v-btn__content,
  ::v-deep .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) .v-icon {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }

  ::v-deep .v-btn.primary.v-btn--disabled:not(.v-btn--outlined):not(.v-btn--text) {
    background-color: rgba(94, 53, 177, 0.35) !important;
    color: rgba(255, 255, 255, 0.55) !important;
  }
}

/* Avatars com contraste garantido (evita primary quebrado do dark theme) */
.vol-avatar {
  background-color: #271540 !important;

  ::v-deep .v-icon,
  .v-icon,
  span {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }
}

.vol-avatar--muted {
  background-color: #eceff1 !important;

  ::v-deep .v-icon,
  .v-icon {
    color: #455a64 !important;
    caret-color: #455a64 !important;
  }
}

.vol-avatar--error {
  background-color: #b71c1c !important;

  ::v-deep .v-icon,
  .v-icon {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }
}

.vol-icon-accent {
  color: #271540 !important;
  caret-color: #271540 !important;
}

.vol-icon-muted {
  color: #90a4ae !important;
  caret-color: #90a4ae !important;
}

.theme--dark .service-volumetry {
  .vol-avatar {
    background-color: #271540 !important;

    ::v-deep .v-icon,
    .v-icon,
    span {
      color: #ffffff !important;
      caret-color: #ffffff !important;
    }
  }

  .vol-avatar--muted {
    background-color: #3c4043 !important;

    ::v-deep .v-icon,
    .v-icon {
      color: #e8eaed !important;
      caret-color: #e8eaed !important;
    }
  }

  .vol-avatar--error {
    background-color: #c54b4b !important;

    ::v-deep .v-icon,
    .v-icon {
      color: #ffffff !important;
      caret-color: #ffffff !important;
    }
  }

  .vol-icon-accent {
    color: #c5b3ff !important;
    caret-color: #c5b3ff !important;
  }

  .vol-icon-muted {
    color: #9aa0a6 !important;
    caret-color: #9aa0a6 !important;
  }
}

.volumetry-tabs-items {
  background-color: transparent !important;

  ::v-deep .v-window__container,
  ::v-deep .v-window-item {
    background-color: transparent !important;
  }
}

.option-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;

  &:hover {
    border-color: #271540 !important;
    background-color: rgba(39, 21, 64, 0.04);
  }

  &--selected {
    border-color: #271540 !important;
    background-color: rgba(39, 21, 64, 0.08);
  }
}

.state-chip {
  min-width: 48px;
}

.success-next-steps {
  max-width: 420px;
}

.zone-panel {
  border: 1px dashed rgba(39, 21, 64, 0.45);
  border-radius: 8px;
  overflow: hidden;

  &--open {
    border-style: solid;
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &__body {
    padding: 0 14px 14px;
  }

  &__footer {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
}

.zone-region-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.zone-card {
  height: 100%;

  &--selected {
    border-color: #ff9f43 !important;
  }

  &--partial:not(.zone-card--selected) {
    border-color: rgba(255, 159, 67, 0.45) !important;
  }

  &__preview {
    line-height: 1.35;
  }
}

.cbo-search-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px dashed rgba(39, 21, 64, 0.45);
  border-radius: 8px;
  background: rgba(39, 21, 64, 0.04);
  cursor: pointer;
  text-align: left;
}

.cbo-family-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.cbo-occupation-row {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  &--selected {
    background: rgba(39, 21, 64, 0.08);
  }
}

.blacklist-panel {
  border: 1px dashed rgba(234, 84, 85, 0.55);
  border-radius: 8px;
  overflow: hidden;

  &--open {
    border-style: solid;
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  &__body {
    padding: 0 14px 14px;
  }
}

.blacklist-item {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &--active {
    border-color: #ea5455 !important;
    background-color: rgba(234, 84, 85, 0.08);
  }
}

.theme--dark {
  .option-card {
    border-color: rgba(255, 255, 255, 0.18) !important;

    &:hover,
    &--selected {
      border-color: #c5b3ff !important;
      background-color: rgba(197, 179, 255, 0.12);
    }
  }

  .zone-panel {
    border-color: rgba(197, 179, 255, 0.45);
  }

  .zone-panel__footer {
    border-top-color: rgba(255, 255, 255, 0.12);
  }

  .cbo-family-card {
    border-color: rgba(255, 255, 255, 0.18);
  }

  .cbo-occupation-row--selected {
    background: rgba(197, 179, 255, 0.14);
  }

  .blacklist-item--active {
    background-color: rgba(234, 84, 85, 0.14);
  }

  .cbo-search-trigger {
    border-color: rgba(197, 179, 255, 0.45);
    background: rgba(197, 179, 255, 0.08);
  }

  /* Badges primary no modal (teleport) — sem depender de .service-volumetry */
  ::v-deep .vol-chip-accent.v-chip,
  .vol-chip-accent {
    background-color: #5e35b1 !important;
    border-color: #5e35b1 !important;
    color: #ffffff !important;
  }

  ::v-deep .vol-chip-accent .v-chip__content,
  ::v-deep .vol-chip-accent .v-icon,
  .vol-chip-accent ::v-deep .v-chip__content,
  .vol-chip-accent ::v-deep .v-icon {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }

  /* Botões primary em dialogs (teleport) */
  ::v-deep .v-dialog .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) {
    background-color: #5e35b1 !important;
    border-color: #5e35b1 !important;
    color: #ffffff !important;
  }

  ::v-deep .v-dialog .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) .v-btn__content,
  ::v-deep .v-dialog .v-btn.primary:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--flat):not(.v-btn--plain) .v-icon {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }
}
</style>
